import type { OrderKpiRange, OrderKpiSettings, OrderKpiValue, PartnerAudience, PartnerCommission } from "@logiful/domain";
import { serverText } from "./server-text";
import type { DeliveryPromise, DeliveryWidgetSettings, Permissions, StoredPermissions, HoldKind, NotificationSettings, NotificationTopic, ProductReplenishment, ReachStatus, ReplenishmentSettings, ReshipReason } from "@logiful/domain";
import type { AddressCheckResult, AddressCheckSettings, OrderRuleSet, Product, ShippingProfile, ShippingRule, ShippingSettings, ValidationIssue } from "@logiful/domain";
import { mockApi } from "./mock";

export type ValidationState = "unchecked" | "valid" | "warnings" | "invalid";

export interface ProductItem extends Product {
  /** Doppelte EAN: die anderen Artikel mit derselben EAN */
  sameGtin?: { id: string; sku: string; name: string }[];
  /** Gewicht stark geändert (> 50 %) – bitte prüfen und bestätigen */
  weightReview?: { from: number; to: number; at: string } | null;
  /** Gewicht stammt aus dem Standardgewicht des Kunden */
  weightIsDefault?: boolean;
  /** Standardgewicht des Kunden (nur in der Einzelansicht) */
  tenantDefaultWeightKg?: number | null;
  /** bestätigte Plausibilitätshinweise: Regel → Fingerabdruck der Werte */
  plausibilityConfirmed?: Record<string, string>;
  validation: { status: ValidationState; issues: ValidationIssue[]; completenessScore: number; checkedAt: string | null };
  lockedFields: string[];
  updatedAt: string;
  /** Stand im Lager: freigegeben = übertragen und für Logiful freigegeben */
  backend?: { status: BackendProductStatus; message: string | null; at: string | null };
  sync?: { system: string; externalId: string; state: string; lastError: string | null; lastSyncedAt: string | null }[];
  history?: { id: string; action: string; actorType: string; /** tatsächliche Quelle (z. B. „Odoo“), vom Server */ actorLabel?: string; message: string | null; changes: Record<string, { from: unknown; to: unknown }> | null; createdAt: string }[];
  /** Sets, in denen dieser Artikel als Komponente steckt */
  usedInBundles?: { id: string; sku: string; name: string; quantity: number }[];
  /** Nur bei Sets: aus Komponenten mögliche Menge, Engpass */
  bundleStock?: BundleStock | null;
  /** Logiful-Location in Shopify je Shop: geführt, ausstehend oder Fehler mit Grund */
  shopLocation?: { channelId: string; channelName: string; state: "stocked" | "pending" | "error"; message: string | null; shop: string }[];
  /** Charge/MHD/Seriennummer/Stückliste gesperrt, solange das Lager Bestand, Anlieferungen oder Aufträge hat */
  structuralLock?: { locked: boolean; reasons: string[] };
  /** Eigene MHD-Schwellen (null = Kunden-Standard) und Chargen im Lager */
  bestBefore?: { warningDays: number | null; criticalDays: number | null; lots: { batch: string | null; bestBefore: string | null; quantity: number; blocked: number }[] };
}

export type BestBeforeLevel = "expired" | "critical" | "warning" | "ok";

export interface BestBeforeLot {
  lotId: string;
  productId: string;
  sku: string;
  name: string;
  batch: string | null;
  bestBefore: string;
  quantity: number;
  blocked: number;
  warehouseRef: string;
  daysLeft: number;
  level: BestBeforeLevel;
  thresholds: { warningDays: number; criticalDays: number };
  customThresholds: boolean;
}

export interface BestBeforeState {
  settings: { warningDays: number; criticalDays: number };
  summary: Record<BestBeforeLevel, { lots: number; units: number }>;
  lots: BestBeforeLot[];
}

export interface BundleStock {
  possibleSets: number;
  bottleneck: string | null;
  components: { productId: string; sku: string; name: string; quantity: number; available: number; possibleSets: number }[];
}

export type BackendProductStatus = "released" | "transferred" | "pending" | "blocked" | "error";
export type BackendFilter = "released" | "transferred" | "missing" | "error";

export interface ProductQuery {
  backend?: BackendFilter;
  search?: string;
  validation?: ValidationState;
  rule?: string;
  weightReview?: "1";
  status?: "draft" | "active" | "archived";
  sort?: "sku" | "name" | "updatedAt" | "completenessScore";
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ProductSummary {
  /** Anzahl aktiver Artikel je Lager-Stand */
  backendCounts?: Partial<Record<BackendFilter, number>>;
  byStatus: Partial<Record<ValidationState, number>>;
  averageCompleteness: number;
  topIssues: { ruleId: string; severity: "error" | "warning" | "info"; example: string; count: number }[];
}

/** Bearbeitbare Felder – entspricht dem PATCH-Schema der API */
export interface ProductPatch {
  name?: string;
  description?: string | null;
  gtin?: string | null;
  mpn?: string | null;
  manufacturer?: string | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  grossWeightKg?: number | null;
  netWeightKg?: number | null;
  hsCode?: string | null;
  countryOfOrigin?: string | null;
  customsValueMinor?: number | null;
  customsDescription?: string | null;
  batchManaged?: boolean;
  bestBeforeManaged?: boolean;
  serialManaged?: boolean;
  fragile?: boolean;
  dangerousGoods?: { unNumber: string; hazardClass?: string; limitedQuantity?: boolean } | null;
  bestBeforeWarningDays?: number | null;
  bestBeforeCriticalDays?: number | null;
  /** Pufferbestand für die Shops (null = Kunden-Standard) */
  stockBuffer?: number | null;
  /** Gebinde (Verpackungseinheiten mit eigener EAN) */
  packUnits?: { name: string; quantity: number; ean: string | null }[];
}

export type OrderStatus = "received" | "on_hold" | "ready" | "submitted" | "in_progress" | "partially_shipped" | "shipped" | "cancelled" | "failed";

export interface OrderItem {
  /** im Testbetrieb verarbeitet – geht nie ans Lager */
  testMode?: boolean;
  id: string;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  customerName: string;
  country: string;
  channelName: string;
  /** Art des Kanals (shopify, odoo, amazon …) – für das Logo */
  channelKind?: string;
  itemCount: number;
  /** Positionen (Vorschau, max. 12) – insert = Beilage aus Regel */
  lines?: { sku: string; name: string; quantity: number; insert?: boolean; imageUrl?: string | null }[];
  /** Teillieferung: noch offene Restlieferungen dieses Auftrags (Auftragsnummern) */
  openRests?: string[];
  /** mehrere Fulfillment-Anfragen derselben Shop-Bestellung */
  shopGroup?: { count: number; numbers: string[]; replacedBy: string | null; earlier?: { id: string; orderNumber: string; status: string; itemCount: number; createdAt: string }[] };
  /** im Shop storniert und neu angefordert – Anzeige „Ersetzt“ (technisch storniert) */
  replaced?: boolean;
  /** versendendes Lager: Kürzel und Name */
  warehouse?: { code: string; name: string };
  totalMinor: number;
  currency: string;
  holdReasons: string[];
  appliedRules: { id: string; name: string }[];
  /** Lieferadresse wurde korrigiert (DHL-Leitdaten oder Logiful) */
  addressCorrected?: boolean;
  tracking?: { carrier: string; numbers: string[] };
  /** Track & Trace über alle Pakete (null = nicht verfolgt) */
  deliveryStatus?: DeliveryStatus | null;
  /** Retourenstand (nur versendete Aufträge): alles zurück, ein Teil zurück, unzustellbar zurück */
  returnState?: "returned" | "partially_returned" | "undeliverable" | null;
  /** Ende des Stornofensters (nur bei „ready“ relevant) */
  releaseAt?: string | null;
  /** Suche: in welchem (sonst nicht sichtbaren) Feld der Begriff gefunden wurde */
  match?: { field: string; value: string };
  // Zusatzspalten (frei wählbar)
  email?: string | null;
  financialStatus?: string;
  priority?: number;
  shippingMethodId?: string | null;
  channelShippingMethod?: string | null;
  shippedAt?: string | null;
  weightKg?: number | null;
  weightIncomplete?: boolean;
  tags?: string[];
  backendRef?: string | null;
}

export interface OrderSearchQuery {
  q?: string;
  /** ein oder mehrere Status, kommagetrennt */
  status?: string;
  channelId?: string;
  country?: string;
  /** YYYY-MM-DD */
  from?: string;
  /** open = offene Restlieferungen aus Teillieferungen */
  partial?: "open";
  /** Sperrgrund-Art (aus „Handlungsbedarf“) */
  hold?: HoldKind;
  /** 1 = ersetzte Anfragen als eigene Zeilen */
  replaced?: "1";
  page?: number;
  pageSize?: 50 | 100 | 250;
  /** Stand der ersten Seite (beim Blättern verschieben neue Aufträge nichts) */
  until?: string;
}

// ── Handlungsbedarf ──────────────────────────────────────────────────
export { HOLD_KIND_LABEL, holdKind, type HoldKind } from "@logiful/domain";
export type ActionSeverity = "critical" | "warning" | "info";
export type ActionArea = "connections" | "orders" | "products" | "stock" | "inbounds" | "returns" | "account";
export interface ActionEntry { key: string; label: string; detail?: string; at?: string; to: string }
export interface ActionItem {
  key: string;
  area: ActionArea;
  severity: ActionSeverity;
  title: string;
  detail: string;
  count: number;
  cta: { label: string; to: string };
  entries: ActionEntry[];
  ackable?: boolean;
  manage?: boolean;
  since?: string;
}
export interface ActionItems {
  items: ActionItem[];
  waiting: ActionItem[];
  counts: Record<ActionSeverity, number> & { total: number };
  generatedAt: string;
}

export interface OrderSearchResult {
  items: OrderItem[];
  counts: Partial<Record<OrderStatus, number>>;
  /** offene Restlieferungen (Teillieferungen) beim Kunden */
  openPartials?: number;
  /** ersetzte Anfragen beim Kunden (für „Ersetzte anzeigen“) */
  replacedCount?: number;
  total: number;
  truncated: boolean;
  page?: number;
  pageSize?: number;
  /** Zeitpunkt der Abfrage – als until für weitere Seiten */
  asOf?: string;
  facets: { channels: { id: string; name: string }[]; countries: string[] };
}
export interface OrderSummary { last24h: { placed: number; shipped: number }; onHold: number; flow: { received: number; on_hold: number; submitted: number; shipped: number } }
export interface Paged<T> { items: T[]; total: number; page: number; pageSize: number }

export interface ShippingDecisionView {
  methodId: string | null;
  customerMethodId: string | null;
  costMinor: number | null;
  packages: { packagingId: string; packaging: string; skus: string[]; weightKg: number; outer: { l: number; w: number; h: number }; fill: number }[];
  hold: string[];
  explanation: { text: string; visibility: "customer" | "internal" }[];
  appliedRules: { id: string; name: string; visibility: "customer" | "internal" }[];
  alternatives: { methodId: string; costMinor: number | null; reason?: string }[];
  missingData: string[];
  decidedAt: string;
}

export type DeliveryStatus = "pre_transit" | "in_transit" | "out_for_delivery" | "pickup_ready" | "delivered" | "exception" | "returned";

export interface TrackingEvent {
  at: string | null;
  text: string;
  ice: string | null;
  location: string | null;
  country: string | null;
}

/** Verfolgung eines Pakets (Track & Trace) */
export interface PackageTracking {
  trackingNumber: string;
  status: DeliveryStatus | null;
  statusText: string | null;
  lastEventAt: string | null;
  deliveredAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  events: TrackingEvent[];
}

/** Track & Trace-Auswertung (je Paket) */
export interface TrackingOverview {
  days: number;
  now: { pre_transit: number; in_transit: number; out_for_delivery: number; pickup_ready: number; exception: number; returned: number; stuck: number; noData: number; deliveredToday: number };
  period: { packages: number; delivered: number; problems: number; returned: number; medianH: number | null; p90H: number | null; buckets: { d1: number; d2: number; d3: number; more: number } };
  prev: { packages: number; delivered: number; problems: number; medianH: number | null };
  daily: { day: string; delivered: number; medianH: number | null }[];
  methods: { method: string; carrier: string | null; packages: number; delivered: number; problems: number; medianH: number | null; within2: number | null }[];
  countries: { country: string; packages: number; delivered: number; medianH: number | null }[];
  regions: { zone: string; packages: number; delivered: number; medianH: number | null }[];
  attention: { orderId: string; orderNumber: string; trackingNumber: string; status: DeliveryStatus | null; statusText: string | null; lastEventAt: string | null; shippedAt: string; reason: "problem" | "returned" | "stuck" | "no_data" | "pickup_ready" }[];
}

export interface CarrierAccount {
  id: string;
  tenantId: string | null;
  owner: "tenant" | "logiful";
  carrier: string;
  carrierName: string;
  label: string;
  username: string;
  dailyLimit: number | null;
  status: "unchecked" | "ok" | "failed";
  checkedAt: string | null;
  lastError: string | null;
  usageToday: { day: string; requests: number; pieces: number } | null;
  methods: string[];
  /** nur Logiful-Übersicht */
  tenantName?: string | null;
}

export interface CarrierInfo {
  key: string;
  name: string;
  hint: string;
}

export interface CarrierAccountsView {
  apiReady: boolean;
  carriers: CarrierInfo[];
  accounts: CarrierAccount[];
  /** Versandarten des Kunden und welches Konto gerade gilt */
  methods: { id: string; carrier: string | null; account: { id: string; label: string; owner: "tenant" | "logiful" } | null }[];
}

export interface AdminCarrierAccountsView {
  apiReady: boolean;
  /** Erkennungsmerkmal des hinterlegten Schlüssels */
  apiKeyHint?: string | null;
  unified?: { today: number; limit: number };
  carriers: CarrierInfo[];
  logiful: CarrierAccount[];
  customers: CarrierAccount[];
  methods: { id: string; carrier: string | null }[];
}

export interface CarrierAccountInput {
  carrier: string;
  label: string;
  username: string;
  password: string;
  methods: string[];
  dailyLimit?: number | null;
}
export type CarrierAccountPatch = Partial<Omit<CarrierAccountInput, "carrier">>;
export type CarrierCheck = { ok: boolean; message: string };

export interface OrderDetail {
  id: string;
  orderNumber: string;
  /** Track & Trace über alle Pakete (null = nicht verfolgt) */
  deliveryStatus?: DeliveryStatus | null;
  deliveredAt?: string | null;
  /** Zusatzinfos aus dem Kanal (z. B. eigene Odoo-Felder) */
  channelFields?: { label: string; value: string }[];
  returnState?: "returned" | "partially_returned" | "undeliverable" | null;
  /** "shopify" | "manual" */
  channelKind?: string;
  /** Nachlieferung zu … */
  reshipOf?: { id: string; orderNumber: string; reason: string | null } | null;
  /** alle Fulfillment-Anfragen derselben Shop-Bestellung (nur wenn mehr als eine), älteste zuerst */
  shopRequests?: { id: string; orderNumber: string; status: string; itemCount: number; createdAt: string; replaced?: boolean }[];
  /** storniert und im Shop neu angefordert: weiter als … */
  replacedBy?: { id: string; orderNumber: string } | null;
  /** Teillieferung: alle Teile (erster Teil zuerst), leer wenn nicht geteilt */
  partialParts?: { id: string; orderNumber: string; status: string }[];
  /** Nachlieferungen dieses Auftrags */
  reships?: { id: string; orderNumber: string; status: string; reason: string | null }[];
  /** noch nicht im Lager → bearbeitbar */
  editable?: boolean;
  /** aus dem Lager übernommene Historie – nur Ansicht */
  importedFrom?: string | null;
  /** im Testbetrieb verarbeitet – geht nie ans Lager */
  testMode?: boolean;
  /** versendendes Lager (nur bei mehreren Lagern) mit Begründung */
  warehouse?: { id: string; code: string | null; name: string; reason: string | null } | null;
  /** nur für Logiful: Ergebnis der Versandart-Wahl */
  shippingDecision?: ShippingDecisionView | null;
  customerShippingMethodId?: string | null;
  /** nur für Logiful: gesendete Optimierungsstufen (FFN-Attribut "optimization") */
  optimizations?: string[];
  status: OrderStatus;
  placedAt: string;
  releaseAt: string | null;
  /** Freigabetermin des Kunden (bis dahin zurückgehalten) */
  deferredUntil?: string | null;
  financialStatus: string;
  channelName: string;
  channelShippingMethod: string | null;
  shippingMethodId: string | null;
  priority: number;
  note: string | null;
  tags: string[];
  shippingAddress: { company?: string; firstName?: string; lastName: string; street: string; houseNumber?: string; addition?: string; zip: string; city: string; country: string; email?: string; phone?: string };
  addressCorrection?: { original: OrderDetail["shippingAddress"]; changes: { field: string; from: string; to: string }[] } | null;
  totalMinor: number;
  currency: string;
  holdReasons: string[];
  /** wartet auf Bestand (Fehlbestand) */
  shortage?: boolean;
  /** Teillieferung möglich: ein Teil jetzt lieferbar, ein Teil wartet */
  partialPossible?: boolean;
  /** „trotz Fehlbestand übertragen“ ausgelöst */
  backorderForcedAt?: string | null;
  /** im FFN nur angelegt (Vorbereitung) – noch nicht bei Logiful */
  warehouseParkedAt?: string | null;
  /** B2B-Lieferung (Modul) */
  b2b?: B2bOrderInfo | null;
  /** Pre-FBA-Sendung (Modul) */
  fba?: FbaOrderInfo | null;
  appliedRules: { id: string; name: string }[];
  lines: { id: string; sku: string; name: string; quantity: number; unitPriceMinor: number | null; productId: string | null; addedByRule?: string | null; blocked: boolean; unknown: boolean; imageUrl?: string | null; /** Gebinde des Artikels – Menge als „2 × Karton 20“ */ packUnits?: { name: string; quantity: number; ean: string | null }[]; /** vorgegebene Chargen mit MHD */ batches?: { batch: string; bestBefore?: string | null; quantity: number }[] }[];
  shipments: {
    id: string;
    carrier: string;
    trackingNumbers: string[];
    trackingUrls: string[];
    shippedAt: string;
    reportedToShop: boolean;
    /** Kanal hat die Versandmeldung abgelehnt – Grund (z. B. Odoo: Charge fehlt) */
    channelError?: string | null;
    lines: { sku: string; quantity: number; batch?: string | null; bestBefore?: string | null }[];
    /** Pakete der Versandmeldung (Inhalt meldet das Lager je Meldung, nicht je Paket) */
    packages?: { trackingNumber: string | null; weight: number | null; dimensions: { length: number; width: number; height: number } | null }[];
    /** Track & Trace je Paket (leer = nicht verfolgt) */
    tracking?: PackageTracking[];
  }[];
  links: { shop: string | null; backendRef: string | null };
  history: { id: string; action: string; actorType: string; actorLabel?: string; message: string | null; createdAt: string }[];
}

/** Shop-Versandart → Lager-Versandart: fixed = gilt immer, preferred = Startwert (Regeln/Optimierung dürfen ändern) */
export interface ShopMethodMapping {
  mappings: { shopMethod: string; methodId: string; mode: "fixed" | "preferred" }[];
  fallbackMethodId: string | null;
}

/** Aufträge mit Fehlbestand: zurückhalten, teilliefern oder übertragen (direkt an Logiful oder nur ins FFN) */
export interface B2bRequirements {
  minShelfLifeDays: number | null;
  fullCartonsOnly: boolean;
  singleBatch: boolean;
}
export type B2bShipping = "freight" | "parcel";
export type B2bLoadCarrier = "pallet" | "carton";
export interface B2bCustomerInput {
  name: string;
  customerNumber: string | null;
  gln: string | null;
  address: Address;
  receivingHours: string | null;
  avisRequired: boolean;
  avisContact: string | null;
  defaultShipping: B2bShipping;
  defaultLoadCarrier: B2bLoadCarrier;
  requirements: B2bRequirements;
  warehouseNote: string | null;
}
export interface B2bCustomer extends B2bCustomerInput {
  id: string;
  archivedAt: string | null;
  deliveries?: number;
  lastDeliveryAt?: string | null;
}
export interface B2bOrderInfo {
  customerId: string;
  customerName: string;
  customerNumber: string | null;
  gln: string | null;
  purchaseOrderNumber: string;
  deliveryDate: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  shipping: B2bShipping;
  loadCarrier: B2bLoadCarrier;
  receivingHours: string | null;
  avisRequired: boolean;
  avisContact: string | null;
  requirements: B2bRequirements;
  warehouseNote: string | null;
}
export interface B2bOrderRow {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  shippedAt: string | null;
  units: number;
  holdReasons: string[];
  b2b: B2bOrderInfo;
}
export interface B2bOrderCreate {
  customerId: string;
  purchaseOrderNumber: string;
  deliveryDate: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  shipping: B2bShipping;
  loadCarrier: B2bLoadCarrier;
  lines: { productId: string; quantity: number; batch?: string }[];
  shippingMethodId?: string;
  note?: string;
  reference?: string;
}

export type FbaPacking = "seller_cartons" | "warehouse_packs";
export type FbaDocumentKind = "carton_labels" | "pallet_labels" | "other";
export interface FbaCarton {
  lines: { sku: string; quantity: number }[];
  weightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}
export interface FbaOrderInfo {
  shipmentId: string | null;
  referenceId: string | null;
  destinationCode: string;
  packing: FbaPacking;
  palletized: boolean;
  fnskuLines: { sku: string; fnsku: string; quantity: number }[];
  packedCartons: FbaCarton[] | null;
  packedAt: string | null;
}
export interface FbaShipmentRow {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  shippedAt: string | null;
  units: number;
  holdReasons: string[];
  fba: FbaOrderInfo;
  destination?: Address;
  documents: FbaDocumentKind[];
}
export interface FbaDocument {
  id: string;
  kind: FbaDocumentKind;
  filename: string;
  size: number;
  createdAt: string;
  sentAt: string | null;
}
export interface FbaShipmentCreate {
  shipmentId?: string;
  referenceId?: string;
  destinationCode: string;
  destination: Address;
  packing: FbaPacking;
  palletized: boolean;
  lines: { productId: string; quantity: number }[];
  shippingMethodId?: string;
  note?: string;
  reference?: string;
}

export interface McpToolSwitches {
  global: Record<string, boolean>;
  tenants: Record<string, Record<string, boolean>>;
}
export interface McpConnections {
  url: string;
  /** Administrator: sieht alle Verbindungen im Team, sonst nur die eigenen */
  manager: boolean;
  licensed: boolean;
  connections: { id: string; client: string; email: string; name: string | null; createdAt: string; lastUsedAt: string | null }[];
  tools: string[];
  endCustomerData: boolean;
}

export interface ReservationSettings {
  strategy: "fifo" | "max_orders";
  channelPriority: string[];
  agingDays: number | null;
  clarificationDays: number | null;
}
export interface OrderReservation {
  strategy: "fifo" | "max_orders";
  covered: boolean;
  position: number | null;
  queued: number;
  klass: "boosted" | "aging" | "channel" | "normal" | "released" | null;
  shortages: { sku: string; needed: number; free: number }[];
  boostedAt: string | null;
  /** Verfügbarkeit je Artikel (Sets aufgelöst; forLines = Positionen, zu denen er gehört) */
  lines?: {
    productId: string;
    forLines: string[];
    sku: string;
    name: string;
    needed: number;
    /** für diesen Auftrag zurückgelegt */
    reserved: number;
    /** verfügbar im Lager (vor der Vergabe) */
    inStock: number;
    /** davon älteren Bestellungen zugeteilt */
    heldByOlder: number;
    holders: { id: string; orderNumber: string; placedAt: string | null; quantity: number }[];
    holderCount: number;
    /** im Lager angekündigt (Wareneingang) */
    incoming: number;
    /** Vorziehen würde die Position sofort lieferbar machen */
    boostWouldCover: boolean;
    /** physisch im Lager (auf Lager) */
    onHand?: number;
    /** davon für übertragene Aufträge im Lager reserviert */
    reservedInWarehouse?: number;
    /** davon gesperrt */
    blocked?: number;
  }[];
}

export interface BackorderSettings {
  mode: "hold" | "transfer" | "partial";
  handover: "fulfiller" | "merchant";
  /** Teillieferung erst ab so viel % lieferbar (leer = keine Grenze) */
  minSharePercent?: number | null;
  /** höchstens so viele Teile je Auftrag (leer = keine Grenze) */
  maxParts?: number | null;
}

/** SFTP-Anbindung: Ordner und Zugang (Passwort/Schlüssel wird nie zurückgegeben) */
export interface SftpConfig {
  host: string;
  port: number;
  username: string;
  authType: "password" | "key";
  inboundDir: string;
  archiveDir: string;
  errorDir: string;
  outboundDir: string;
  templateId: string | null;
  exportShipments: boolean;
}
export interface SftpChannel {
  id: string;
  name: string;
  status: ChannelItem["status"];
  lastError: string | null;
  config: SftpConfig;
  hasSecret: boolean;
  log: {
    lastRunAt: string;
    lastError: string | null;
    files: { name: string; at: string; result: "ok" | "error"; created: number; duplicates: number; issues: number; message?: string }[];
    lastExport?: { name: string; at: string; shipments: number };
  } | null;
}

/** Verkaufshistorie aus Shopify je Shop (Statistik für Reichweite) */
export interface SalesHistoryShop {
  channelId: string;
  name: string;
  active: boolean;
  /** ab hier zählt Connect selbst (erster Auftrag des Shops in Connect) */
  connectFrom: string | null;
  state: {
    status: "waiting" | "exporting" | "importing" | "done" | "failed";
    startedAt: string;
    finishedAt?: string;
    exported?: number;
    requestedFrom: string;
    firstDay?: string | null;
    lastDay?: string | null;
    orders?: number;
    skus?: number;
    error?: string;
  } | null;
}

/** Abgefragte Shops/Marktplätze */
export type MarketplaceKind = "woocommerce" | "shopware" | "kaufland" | "amazon" | "odoo";
export const MARKETPLACE_KIND_LIST: MarketplaceKind[] = ["woocommerce", "shopware", "kaufland", "amazon", "odoo"];

/** Odoo: Zuordnung eines eigenen Feldes */
export interface OdooFieldMap {
  model: "sale.order" | "stock.picking" | "res.partner";
  field: string;
  label: string;
  target: "note" | "tag" | "info" | "shipping_method";
}
/** Artikel-Feldzuordnung Odoo → Connect */
export interface OdooProductFieldMap { field: string; target: "grossWeightKg" | "netWeightKg" | "lengthCm" | "widthCm" | "heightCm" | "gtin" | "hsCode" | "countryOfOrigin" | "mpn" | "manufacturer" | "customsDescription"; unit?: "kg" | "g" | "cm" | "mm" | "m" }
export interface OdooDescribe {
  version: string;
  /** Felder am Odoo-Artikel (Zahl, Text, Verknüpfung) für die Artikel-Zuordnung */
  productFields?: { name: string; label: string; type: string; custom: boolean }[];
  /** Unternehmen, auf die der Odoo-Benutzer Zugriff hat */
  companies?: { id: number; name: string }[];
  warehouses: { id: number; name: string; code: string | null; companyId?: number | null }[];
  fields: Record<OdooFieldMap["model"], { name: string; label: string; type: string; custom: boolean }[]>;
}
export const isMarketplace = (k: string | undefined): k is MarketplaceKind => !!k && (MARKETPLACE_KIND_LIST as string[]).includes(k);
export interface MarketplaceChannel {
  id: string;
  kind: MarketplaceKind;
  label: string;
  name: string;
  status: ChannelItem["status"];
  lastError: string | null;
  config: Record<string, unknown>;
  pushStock: boolean;
  /** unbekannte Artikel aus Aufträgen automatisch anlegen (Standard an) */
  autoCreateProducts?: boolean;
  stockSync?: import("@logiful/domain").ChannelStockSync;
  importFrom: string | null;
  hasSecret: boolean;
  /** im gebuchten Modul enthalten */
  licensed: boolean;
  log: {
    lastRunAt: string;
    lastError: string | null;
    lastOrderAt: string | null;
    runs: { at: string; created: number; cancelled: number; shipped: number; stock: number; products?: number; error?: string }[];
    stockNotFound: string[];
    /** Artikel mit abweichender Chargen-/MHD-Pflicht (Kanal ≠ Connect) */
    productMismatch?: string[];
    lastFullStockAt: string | null;
  } | null;
}

export type { ChannelStockSync } from "@logiful/domain";
export interface ChannelItem {
  id: string;
  /** Bestand an diesen Kanal abweichend (null = Standard) */
  stockSync?: import("@logiful/domain").ChannelStockSync;
  kind: "shopify" | "sftp" | MarketplaceKind;
  name: string;
  externalAccount: string;
  status: "pending" | "active" | "error" | "disconnected";
  lastError: string | null;
  productCount: number;
  lastSyncAt: string | null;
}

export interface ImportFilter {
  mode: "all" | "filtered";
  statuses: ("ACTIVE" | "DRAFT" | "ARCHIVED")[];
  vendors: string[];
  productTypes: string[];
  tagsAny: string[];
  tagsExclude: string[];
  skuPrefixes: string[];
  onlyTracked: boolean;
  onlyShippable: boolean;
  /** Varianten ohne SKU: überspringen oder Shopify-Varianten-ID als SKU */
  skuFallback?: "skip" | "variantId";
}

export interface ReleaseResult {
  queued: number;
  alreadyReleased: number;
  notTransferred: { sku: string; reason: string }[];
}

/** CSV-Import: gespeicherte Vorlage (Spaltenzuordnung) */
export interface CsvTemplate {
  id: string;
  kind: "orders" | "products";
  name: string;
  delimiter: ";" | "," | "	" | "|" | null;
  hasHeader: boolean;
  mapping: Record<string, string>;
  defaults: Record<string, string>;
}
export type CsvTemplateInput = Omit<CsvTemplate, "id">;
export interface CsvImportBody {
  csv: string;
  templateId?: string;
  template?: Omit<CsvTemplateInput, "name"> & { name?: string };
  fileName?: string;
  transfer?: boolean;
}
export interface CsvIssue { line: number; message: string }
export interface OrderCsvPreview {
  header: string[]; missingColumns: string[]; rows: number; orders: number; lines: number; duplicates: string[]; unknownSkus: string[]; issues: CsvIssue[]; issueCount: number;
  sample: { orderNumber: string; name: string; city: string; country: string; shippingMethod: string | null; lines: string }[];
}
export interface ProductCsvPreview {
  header: string[]; missingColumns: string[]; rows: number; create: number; update: number; issues: CsvIssue[]; issueCount: number;
  sample: { sku: string; isNew: boolean; fields: string[] }[];
}

// ── Retouren-Auswertung ──────────────────────────────────────────────
export interface ReturnsAnalytics {
  days: number;
  totals: { cases: number; units: number; sold: number; rate: number | null; undeliverable: number; conditions: { A: number; B: number; C: number; none: number }; prev: { units: number; sold: number; rate: number | null } };
  reasons: { id: string; label: string; units: number }[];
  weekly: { week: string; units: number; sold: number }[];
  products: { productId: string; sku: string; name: string; sold: number; returned: number; rate: number | null; A: number; B: number; C: number; topReason: string | null; flagged: boolean }[];
  unmatched: number;
  unmatchedSample?: { sku: string | null; jfsku: string | null; name: string | null; units: number }[];
}

// ── Leistungskennzahlen ──────────────────────────────────────────────
export interface Performance {
  days: number;
  totals: { received: number; shipped: number; measured: number; onTime: number; onTimeRate: number | null; medianHandoverH: number | null; p90HandoverH: number | null; medianOrderH: number | null; prev: { received: number; shipped: number; onTimeRate: number | null } };
  daily: { day: string; received: number; shipped: number; onTime: number; measured: number }[];
  carriers: { carrier: string; shipped: number; onTimeRate: number | null; medianHandoverH: number | null }[];
  backlog: { open: number; overdue: number; oldestHandedOverAt: string | null; overdueOrders: { id: string; orderNumber: string; handedOverAt: string; deadline: string }[] };
}

// ── Benachrichtigungen ───────────────────────────────────────────────
export type { NotificationSettings, NotificationTopic, NotificationMode } from "@logiful/domain";
export interface NotificationState {
  settings: NotificationSettings;
  topics: Record<NotificationTopic, { label: string; description: string }>;
  email: string;
  staff: boolean;
  /** von Logiful freigegeben (Hauptschalter) – sonst werden Einstellungen gespeichert, aber noch keine E-Mails versendet */
  active: boolean;
}

// ── Reichweite & Nachbestellung ──────────────────────────────────────
export type { ReachStatus, ReplenishmentSettings, ProductReplenishment } from "@logiful/domain";
export type SlowMoverClass = "dead" | "slow" | "new" | "ok";
export interface SlowMoverItem {
  productId: string;
  sku: string;
  name: string;
  supplier: string | null;
  cls: SlowMoverClass;
  onHand: number;
  lastSale: string | null;
  lastSaleDays: number | null;
  sold90: number;
  sold365: number;
  daily: number;
  reachDays: number | null;
  excess: number;
  purchasePrice: number | null;
  capital: number | null;
  excessCapital: number | null;
  storageMonthly: number | null;
  storageDays: number | null;
  storageSinceInbound: number | null;
  lastInbound: string | null;
  discontinued: boolean;
  advice: string;
}
export interface SlowMoverQuery { filter?: SlowMoverClass | "action" | "all"; search?: string; sort?: "capital" | "units" | "lastSale" | "sku"; page?: number; pageSize?: number }
export interface SlowMoverPage {
  items: SlowMoverItem[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<SlowMoverClass, number>;
  summary: { excessUnits: number; excessCapital: number | null; withoutPrice: number; stockCapital: number | null; storageMonthly: number | null; storageSinceInbound: number | null; withoutVolume: number };
  settings: { deadDays: number; overstockDays: number; storageCostPerM3Day: number | null };
}
export { REACH_STATUS_LABEL } from "@logiful/domain";
export interface ReachQuery {
  search?: string;
  filter?: ReachStatus | "action" | "all";
  supplier?: string;
  sort?: "urgency" | "sku" | "daily" | "daysLeft";
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  window?: 14 | 30 | 60 | 90;
}
export interface ReachItem {
  productId: string;
  sku: string;
  name: string;
  status: ReachStatus;
  free: number;
  incoming: number;
  daily: number;
  soldWindow: number;
  sold90: number;
  daysLeft: number | null;
  daysWithIncoming: number | null;
  orderInDays: number | null;
  suggested: number;
  leadDays: number;
  safetyDays: number;
  supplier: string | null;
  packSize: number | null;
  minQty: number | null;
  supplierSku?: string | null;
  purchasePrice?: number | null;
  custom: boolean;
  nextInbound: { at: string | null; quantity: number } | null;
  weekly: number[];
}
export interface ReachPage {
  items: ReachItem[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<ReachStatus, number>;
  settings: ReplenishmentSettings;
  suppliers: string[];
}

export interface StockPageQuery {
  search?: string;
  filter?: "all" | "instock" | "out" | "low" | "incoming";
  sort?: "sku" | "available" | "onHand";
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  low?: number;
}
export interface StockPage {
  items: StockItem[];
  total: number;
  page: number;
  pageSize: number;
  summary: { all: number; instock: number; out: number; low: number; incoming: number; units: number; lastUpdate: string | null };
}

export interface ShopifyImportState {
  runId: string;
  status: "awaiting_filter" | "exporting" | "importing" | "done" | "failed";
  filter: ImportFilter;
  catalogSize?: number;
  exported: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  /** Anzahl nicht übernommener Varianten je Grund */
  skipReasons?: Record<string, number>;
  /** Varianten, bei denen der Kunde handeln muss (fehlende SKU, Fehler) */
  problems?: { reason: "no_sku" | "error"; productId: string; variantId: string; title: string; message?: string }[];
  startedAt: string;
  finishedAt?: string;
  error?: string;
  lastActivityAt: string | null;
}

export interface ShopStockPush {
  status: "running" | "done" | "failed";
  startedAt: string;
  finishedAt?: string;
  products: number;
  units: number;
  withoutStock: number;
  notAtLocation: number;
  error?: string | null;
}

export interface ChannelDetail {
  kind?: "shopify" | "sftp" | MarketplaceKind;
  stockSync?: import("@logiful/domain").ChannelStockSync;
  id: string;
  name: string;
  externalAccount: string;
  status: ChannelItem["status"];
  lastError: string | null;
  ownApp: boolean;
  filter: ImportFilter;
  filterSaved: boolean;
  import: ShopifyImportState | null;
  /** Logiful-Standort in Shopify: an allen importierten Artikeln oder nur an für Logiful freigegebenen */
  locationScope?: "all" | "released";
  /** Fehlbestand je Shop (leer = wie Kunden-Einstellung) */
  backorder?: Partial<BackorderSettings>;
  /** Fulfillment in Shopify automatisch anfordern */
  autoRequestFulfillment?: "all" | "paid" | "off";
  /** Tracking-Link in Shopify: wie gemeldet oder immer DHL-Sendungsverfolgung (dhl.de) */
  trackingLink?: "warehouse" | "dhl_de";
  /** Zuordnung Shop-Versandart → Lager-Versandart */
  shippingMapping?: ShopMethodMapping;
  /** in Aufträgen vorgekommene Shop-Versandarten */
  shopMethodsSeen?: { name: string; count: number }[];
  /** wer Versand-/Zustell-Mails an Endkunden verschickt (Shopify-Shops) */
  deliveryNotifications?: "none" | "shopify" | "klaviyo";
  klaviyoConnected?: boolean;
  /** von Shopify abgelehnte Anforderungen */
  refusedRequests?: { fulfillmentOrderId: string; orderNumber: string; reason: string; at: string; shopUrl: string }[];
  /** letzte Übertragung „Alle Bestände an Shopify“ */
  stockPush?: ShopStockPush | null;
  /** Bestand an Shopify melden (aus = Kunde pflegt Bestand in Shopify) */
  pushStock?: boolean;
  /** Varianten ohne SKU in Shopify (nicht an der Logiful-Location) */
  missingSku?: { count: number; items: { productId: string; sku: string; name: string }[] };
  /** fehlende SKU automatisch in Shopify ergänzen */
  fillMissingSku?: boolean;
  /** nächtlicher Artikel-Abgleich + letzter Lauf */
  nightlyProductSync?: boolean;
  nightlyProductSyncAt?: string | null;
}

export interface StockItem {
  productId: string;
  sku: string;
  name: string;
  onHand: number;
  available: number;
  reserved: number;
  blocked: number;
  announced: number;
  /** im Portal reserviert: Aufträge, die noch nicht im Lager sind (eingegangen, zurückgehalten, Stornofenster) */
  portalReserved?: number;
  /** Pufferbestand, der nie gemeldet wird */
  buffer?: number;
  /** an die Shops gemeldeter Bestand */
  sellable?: number | null;
  /** null = noch nie an Shop gemeldet */
  inSyncWithShop: boolean | null;
  updatedAt: string | null;
}

export type InboundStatus = "draft" | "announced" | "partially_received" | "received" | "closed" | "cancelled";

export interface InboundItem {
  id: string;
  inboundNumber: string;
  status: InboundStatus;
  expectedAt: string | null;
  supplier: string | null;
  purchaseOrderNumber: string | null;
  note: string | null;
  /** Ziel-Lager */
  warehouseRef?: string | null;
  warehouseName?: string | null;
  /** fester Anliefertermin (deutsche Zeit) */
  appointment?: InboundAppointment | null;
  createdAt: string;
  /** je Charge eine Position: batch/bestBefore = angekündigte Charge bzw. MHD (YYYY-MM-DD) */
  lines: { id: string; productId: string; sku: string; name: string; quantityExpected: number; quantityReceived: number; batch?: string | null; bestBefore?: string | null }[];
}

export interface NewInbound {
  /** Ziel-Lager (bei mehreren Lagern); leer = Hauptlager */
  warehouseRef?: string;
  expectedAt?: string;
  supplier?: string;
  purchaseOrderNumber?: string;
  note?: string;
  lines: { productId: string; quantity: number; batch?: string; bestBefore?: string }[];
}

export interface RulesState {
  active: { version: number; definition: OrderRuleSet; createdAt: string | null };
  versions: { version: number; isActive: boolean; createdAt: string }[];
}

export interface RulesPreview {
  total: number;
  released: number;
  held: number;
  rejected: number;
  orders: { orderId: string; orderNumber: string; decision: "release" | "hold" | "reject"; holdReasons: string[]; rejectReason: string | null; shippingMethodId: string | null; appliedRules: { id: string; name: string }[]; addLines?: { sku: string; quantity: number }[] }[];
}

export type StaffRole = "staff_admin" | "staff";
export type TenantRole = "customer_admin" | "customer_user";
export type EffectiveRole = StaffRole | TenantRole;

/** beim Lager, Hausnummer nur im Adresszusatz (vor der Grundbereinigung übergeben) */
export interface HouseNumberInAddition {
  orderId: string;
  tenantId: string;
  tenant: string;
  customerNumber: string | null;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  street: string;
  addition: string;
  suggestion: { street: string; addition: string };
}

/** Shop-Check: ein Prüfpunkt */
export interface ShopHealthCheck {
  key: string;
  title: string;
  status: "ok" | "warn" | "error" | "manual" | "unknown";
  detail: string;
  fix?: "webhooks" | "location_activate" | "location_online" | "stock_location" | "setup" | "shipping_profiles";
  fixLabel?: string;
  link?: { label: string; url: string };
}

/** Ausfuhrbegleitdokument eines Auftrags */
export interface ExportDocumentState {
  documents: { id: string; filename: string; size: number; createdAt: string; sentAt: string | null }[];
  approvedAt: string | null;
  approvedBy: string | null;
  editable: boolean;
}
export interface ExportDocumentSettings { enabled: boolean; valueThresholdEur: number; weightThresholdKg: number }

export interface Me {
  user: { id: string; email: string; name: string | null; isStaff: boolean; staffRole: StaffRole | null; /** Sprache des Portals (Profil) */ locale?: "de" | "en" };
  /** gesehene Rundgänge (Name → Version) */
  onboarding?: Record<string, number>;
  /** testModeSince: Testbetrieb (Hardlock) aktiv – nichts ans Lager, keine Bestände/Standort an Shops */
  tenants: { tenantId: string; name: string; slug: string; role: EffectiveRole; testModeSince?: string | null; customerNumber?: string | null; permissions?: Permissions; /** freigeschaltete Funktionen je Modul */ modules?: Partial<Record<ModuleKey, string[]>>; /** Interessent (selbst registriert, noch nicht freigeschaltet): freigegebene Bereiche */ prospect?: import("@logiful/domain").ProspectAccess | null; /** Banner „überfällige Rechnungen“ (nur wenn sichtbar) */ overdue?: { count: number; amount: number; oldestDays: number; text: { de: string; en: string }; blocked?: boolean; pauseDate?: string | null; checkedAt: string | null } | null }[];
  /** Welche Anbindungen serverseitig eingerichtet sind */
  features?: { shopify: boolean; ffn: boolean; mail: boolean };
  /** Zwei-Faktor: Stand der Sitzung (pending = Code eingeben, setup = Einrichtung vorgeschrieben) */
  mfa?: { state: "ok" | "pending" | "setup"; enabled: boolean };
}

// ── Lieferzeit im Shop ───────────────────────────────────────────────
export type { DeliveryWidgetSettings, DeliveryPromise } from "@logiful/domain";
export interface DeliveryWidgetState { settings: DeliveryWidgetSettings; key: string | null; effectiveCutoff: string; snippets: { shopify: string; html: string } | null }

// ── Kosten ───────────────────────────────────────────────────────────
export interface CustomerCosts {
  enabled: boolean;
  customerNumber: string;
  invoices: { id: number; number: string | null; date: string | null; periodStart: string; periodEnd: string; net: number; gross: number; paid: boolean; paymentMethod: string | null; shippedOrders: number; costPerOrder: number | null; groups: { group: string; net: number }[] }[];
  months: { month: string; net: number; shippedOrders: number }[];
  groups12m: { group: string; net: number }[];
  pending: { items: { group: string; position: string | null; quantity: number; unitPrice: number; discountPct: number; net: number; date: string | null; unit: string | null }[]; net: number };
  current: { since: string; shippedOrders: number; avgCostPerOrder: number | null; estimate: number | null };
}

// ── Schnittstelle ────────────────────────────────────────────────────
export { WEBHOOK_EVENTS, WEBHOOK_EVENT_KEYS } from "@logiful/domain";
/** Werkzeugkasten (Logiful-Admin): Auftrag zum Simulieren */
export interface DevOrder {
  id: string;
  orderNumber: string;
  status: string;
  channelKind: string | null;
  channelName: string | null;
  testMode: boolean;
  /** Aktionen wirken nach außen (echter Kunde ohne Testmodus, Produktion) */
  live: boolean;
  lines: { sku: string; name: string; quantity: number; batches: { batch: string; bestBefore?: string | null; quantity: number }[] }[];
  shipments: number;
  trackingNumbers: string[];
  deliveryStatus: string | null;
  /** simulierte Sendungen – änderbar und zurücksetzbar */
  simulated: { id: string; carrier: string; trackingNumbers: string[]; lines: DevShipLine[]; reported: boolean; channelError: string | null }[];
}
export type DevShipLine = { sku: string; quantity: number; batch?: string | null; bestBefore?: string | null };
/** Gezielter Abruf aus einem Kanal (Nummern oder Zeitraum) */
export interface ChannelOrderQuery { numbers?: string[]; from?: string; to?: string }
export interface ChannelOrderHit {
  externalId: string;
  number: string;
  placedAt: string;
  status: "open" | "cancelled";
  channelState: string | null;
  shippable: boolean;
  recipient: string;
  city: string;
  country: string;
  lines: { sku: string; name: string; quantity: number; known: boolean }[];
  known: { id: string; orderNumber: string; status: string } | null;
  /** nicht übernehmbar (z. B. Shopify: anderer Standort, erledigt) – Grund */
  blocked?: string | null;
}
export interface ChannelOrderImport { created: { id: string; orderNumber: string }[]; requested?: { number: string }[]; skipped: { number: string; reason: string }[] }
export interface ChannelOrderSearch { truncated: boolean; notFound: string[]; /** Grund je nicht gefundener Nummer (z. B. Odoo: anderes Lager) */ notFoundReasons?: Record<string, string>; orders: ChannelOrderHit[] }
/** Tool-Anbindungen (Klaviyo) */
export interface AppsView {
  licensed: boolean;
  eventGroups: { key: string; label: string; hint: string; events: string[] }[];
  klaviyo: { id: string; active: boolean; events: string[]; account: string | null; disabledReason: string | null; consecutiveFailures: number; lastSuccessAt: string | null; lastFailureAt: string | null; createdAt: string } | null;
}
export interface KlaviyoDelivery { id: string; event: string; status: "pending" | "success" | "failed"; attempts: number; orderNumber: string | null; error: string | null; note: string | null; createdAt: string; deliveredAt: string | null }
export interface ApiKeyItem { id: string; name: string; prefix: string; permissions: Permissions; preset: string; createdBy: string | null; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }
export interface WebhookItem { id: string; url: string; description: string | null; events: string[]; active: boolean; disabledReason: string | null; consecutiveFailures: number; lastSuccessAt: string | null; lastFailureAt: string | null; createdAt: string; pending: number; failed: number; delivered24h: number }
export interface WebhookDelivery { id: string; event: string; status: "pending" | "success" | "failed"; attempts: number; nextAttemptAt: string; responseStatus: number | null; error: string | null; createdAt: string; deliveredAt: string | null; payload: unknown }

// ── Sicherheit ───────────────────────────────────────────────────────
export interface MfaStatus { enabled: boolean; enabledAt: string | null; recoveryRemaining: number; required: boolean; state: "ok" | "pending" | "setup" }
export interface TeamSecurity { requireMfa: boolean; members: { userId: string; email: string; name: string | null; role: string; mfa: boolean; lastLoginAt: string | null }[] }
export type AuditArea = "orders" | "products" | "inbounds" | "settings" | "team" | "security";
export interface AuditEntry {
  id: string;
  at: string;
  action: string;
  message: string | null;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  actor: { type: string; id: string | null; name: string | null; email: string | null; staff: boolean };
  entity: { type: string; id: string; label: string | null };
}
export interface AuditQuery { actor?: "people" | "all" | "system"; userId?: string; area?: AuditArea; search?: string; before?: string }

export interface BackendState {
  /** FFN-Client serverseitig eingerichtet? */
  configured: boolean;
  connection: null | {
    id: string;
    kind: string;
    status: "pending" | "active" | "error" | "disconnected";
    /** false = Logiful hat den FFN-Händler nur zugeordnet, Freigabe bei JTL steht aus */
    authorized: boolean;
    merchantId: string | null;
    merchantCompany: string | null;
    warehouseId: string | null;
    warehouseName: string | null;
    carriers: number;
    lastError: string | null;
    connectedAt: string;
    streams: { stream: string; lastRunAt: string | null; lastError: string | null }[];
    /** Letzter Artikelimport aus dem Lager (Migration), null = noch nie */
    productImport?: ProductImportState | null;
    /** Letzte Übernahme der Auftragshistorie aus dem Lager */
    orderImport?: OrderImportState | null;
  };
}

export interface OrderImportState {
  runId: string;
  status: "running" | "done" | "failed";
  startedAt: string;
  finishedAt?: string;
  since: string | null;
  withShipments: boolean;
  pages: number;
  total?: number;
  created: number;
  updated: number;
  skipped: number;
  shipments: number;
  lastActivityAt: string | null;
  error: string | null;
}

export interface ProductImportState {
  runId: string;
  status: "running" | "done" | "failed";
  startedAt: string;
  finishedAt?: string;
  lastActivityAt: string | null;
  pages: number;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  bundlesLinked: number;
  /** Stücklisten, deren Komponenten im Lager fehlen */
  pendingBundles: number;
  error: string | null;
}

export interface ShippingMethod {
  id: string;
  /** Name im Lager (intern, z. B. „DHL LOGIFUL“) */
  name: string;
  /** Bezeichnung für Kunden (z. B. „DHL Paket“) – vom Server, sonst wie name */
  label?: string;
  carrier: string | null;
  /** von Logiful hinterlegt: geht so an Shopify (sonst Angaben des Lagers) */
  tracking?: { carrierName: string | null; urlTemplate: string | null } | null;
  carrierCode: string | null;
  type: "Standard" | "Expedited" | "NextDay" | "SecondDay" | "SameDay" | "ByShippingLabelProvider" | null;
  cutoffTime: string | null;
  note: string | null;
}

export interface ShippingMethodsState {
  activeWarehouseId: string | null;
  /** zuletzt von FFN abgerufen */
  syncedAt?: string;
  warehouses: { warehouseId: string; name: string; city: string | null; country: string | null; active: boolean; primary?: boolean; priority?: number; shippingMethods: ShippingMethod[] }[];
  routing?: WarehouseSettings["routing"];
}

/** Mehrere Lager: aktive Lager, Hauptlager, Reihenfolge, Länderregeln */
export interface WarehouseSettings {
  primaryId: string;
  warehouses: { warehouseId: string; active: boolean; priority: number }[];
  routing: { countryRules: { countries: string[]; warehouseId: string }[]; fallbackToOther: boolean };
}

export type Address = OrderDetail["shippingAddress"];

export interface AddressBookEntry {
  id: string;
  label: string | null;
  address: Address;
  useCount: number;
  lastUsedAt: string | null;
}

export interface NewManualOrder {
  reference?: string;
  address: Address;
  lines: { productId: string; quantity: number }[];
  shippingMethodId?: string;
  note?: string;
  express?: boolean;
  saveToAddressBook?: boolean;
  addressBookLabel?: string;
  addressBookId?: string;
  /** Nachlieferung zu diesem Auftrag */
  reshipOf?: string;
  reshipReason?: ReshipReason;
}

export interface PackagingItem {
  id: string;
  name: string;
  kind: "box" | "bag";
  warehouseRef: string | null;
  innerLengthCm: number;
  innerWidthCm: number;
  innerHeightCm: number;
  tareKg: number;
  maxKg: number;
  costMinor: number;
  active: boolean;
}
export type PackagingInput = Omit<PackagingItem, "id">;

export interface ShippingProfilesState {
  profiles: { methodId: string; scope?: "global" | "tenant"; definition: ShippingProfile; updatedAt: string }[];
  available: { methodId: string; name: string; carrier: string | null; type: string | null; warehouses: string[] }[];
}

export interface ShippingRulesState {
  version: number;
  rules: ShippingRule[];
  /** nur für Logiful */
  settings?: ShippingSettings;
}

export interface SimulationResult {
  orders: number;
  days: number;
  current: { byMethod: Record<string, number>; costMinor: number; unpriced: number; holds: number };
  proposed: { byMethod: Record<string, number>; costMinor: number; unpriced: number; holds: number };
  savingsMinor: number;
  changed: number;
  examples: { orderNumber: string; from: string | null; to: string | null; savingMinor: number | null }[];
}

export type { Permissions, StoredPermissions, Area, PermissionPreset } from "@logiful/domain";
export { AREAS, AREA_KEYS, PERMISSION_PRESETS, can, effectivePermissions, presetOf } from "@logiful/domain";
export interface TeamState {
  myRole: EffectiveRole;
  members: { userId: string; email: string; name: string | null; role: TenantRole; lastLoginAt: string | null; joinedAt: string; permissions?: Permissions; preset?: string }[];
  invites: { id: string; email: string; role: TenantRole; expiresAt: string; createdAt: string }[];
}

/** Retouren aus der Retouren-Bearbeitung (Retool) – übernommen und verknüpft */
export type ReturnStatus = "checked" | "notified" | "shop_synced" | "shop_sync_failed";
/** Retouren an Shopify melden: über Connect, über die eigene Shopify-App des Kunden oder aus */
export interface ReturnsShopifySettings { mode: "connect" | "own_app" | "off"; autoRefund: boolean; activeSince: string; reportUndeliverable?: boolean }
export interface ReturnLinkedTenant {
  tenantId: string;
  name: string;
  key: string;
  cases: number;
  shopify: ReturnsShopifySettings | null;
  /** die eigene Shopify-App des Kunden hat in den letzten 30 Tagen Retouren gemeldet */
  ownAppSeen: boolean;
  shopifyDone: number;
  shopifyFailed: number;
  shopifyLastError: string | null;
}

export type ProfitSettings = import("@logiful/domain").ProfitSettings;
export type ProductRuleSet = import("@logiful/domain").ProductRuleSet;
export interface ValidationSettingsView { shipsOutsideEu: boolean; editable: boolean; rules: { id: string; description: string; category: string; configurable: boolean; defaultLevel: "error" | "warning" | "info"; level: "error" | "warning" | "info" | "off" }[] }
export interface ProductRulesState { version: number | null; definition: ProductRuleSet; versions: { version: number; isActive: boolean; createdAt: string; rules: number }[] }
export interface ProductRulePreview { checked: number; affected: number; items: { productId: string; sku: string; name: string; rules: string[]; changes: { field: string; from: string; to: string }[] }[] }
export interface ProfitSums { orders: number; revenueNetMinor: number; cogsMinor: number; feesMinor: number; fulfillmentMinor: number; otherMinor: number; refundsNetMinor: number; contributionMinor: number }
/** von Hand erfasste Kosten im Profit Center */
export interface ProfitManualCost { id: string; kind: "monthly" | "order"; label: string; category: string; amountEur: number; startMonth: string | null; endMonth: string | null; orderId: string | null; orderNumber: string | null; placedAt: string | null; note: string; createdAt: string }
export type ProfitManualCostInput =
  | { kind: "monthly"; label: string; category: string; amountEur: number; startMonth: string; endMonth: string | null; note?: string }
  | { kind: "order"; label: string; category: string; amountEur: number; orderId?: string; orderNumber?: string; note?: string };
export interface ProfitReport {
  from: string;
  to: string;
  totals: ProfitSums & { marginPct: number | null; estimatedOrders: number; missingCostOrders: number; fixedCostsMinor: number; resultMinor: number };
  fixedCosts: { id: string; label: string; category: string; monthlyMinor: number; periodMinor: number }[];
  days: { day: string; revenueNetMinor: number; contributionMinor: number }[];
  channels: (ProfitSums & { channelId: string; name: string; marginPct: number | null })[];
  products: { productId: string | null; sku: string; name: string; units: number; revenueNetMinor: number; cogsMinor: number | null; contributionMinor: number | null; marginPct: number | null }[];
  orders: { key: string; orderId: string | null; orderNumber: string; placedAt: string; channelId: string; exact: boolean; missingCostSkus: string[]; revenueNetMinor: number; cogsMinor: number; feesMinor: number; fulfillmentMinor: number; otherMinor: number; refundsNetMinor: number; contributionMinor: number; marginPct: number | null }[];
  missingCost: { productId: string | null; sku: string; name: string; units: number }[];
  fulfillmentIncluded: boolean;
  fulfillmentAvailable: boolean;
  fulfillmentNote: string | null;
}
export interface ProductCostItem { id: string; sku: string; name: string; costPrice: number | null; purchasePrice: number | null; effectiveCost: number | null; source: "average" | "purchase" | "bundle" | null; costUpdatedAt: string | null }
export interface ProductCostEvent { id: string; kind: "inbound" | "manual"; quantity: number | null; unitCost: number | null; stockBefore: number | null; costBefore: number | null; costAfter: number; inboundId: string | null; inboundNumber: string | null; createdAt: string }
export interface ProfitInbound { id: string; number: string; status: string; supplier: string | null; purchaseOrderNumber: string | null; createdAt: string; lines: { id: string; inboundId: string; productId: string; sku: string; name: string; expected: number; received: number; unitCost: number | null; applied: number }[] }
export interface ProfitShopStatus { id: string; name: string; kind: string; exact: boolean; lastRunAt: string | null; lastError: string | null; orders: number }
export type ModuleKey = "address_check" | "returns_portal" | "channels" | "shop_widgets" | "analytics" | "api" | "customer_comms" | "purchasing" | "b2b" | "fba" | "ai" | "tracking" | "apps" | "profit" | "production" | "tracking_page";
export type ProductionStatus = import("@logiful/domain").ProductionStatus;
export type ProductionKind = import("@logiful/domain").ProductionKind;
export type ProductionBomDef = import("@logiful/domain").ProductionBom;
export type ProductionBomInput = ProductionBomDef;
export type QuoteLine = import("@logiful/domain").QuoteLine;
export interface ProductionQuoteView { lines: QuoteLine[]; note: string; totalMinor: number }
export interface ProductionFile { id: string; kind?: "instruction" | "sample"; filename: string; mimeType: string; size: number; createdAt: string }
export interface ProductionBomItem { productId: string; sku: string; name: string; version: number; archived: boolean; updatedAt: string; components: number; materials: number; steps: number; sampleRequired: boolean }
export interface ProductionBomDetail { product: { id: string; sku: string; name: string }; version: number; archived: boolean; bom: ProductionBomDef | null; items: { id: string; sku: string; name: string; status: string; available: number }[]; files: ProductionFile[]; pricesEditable: boolean }
export interface ProductionOrderRow {
  id: string; number: string; kind: ProductionKind; auto: boolean; product: { id: string; sku: string; name: string }; quantity: number; status: ProductionStatus; dueDate: string | null; plannedDate: string | null;
  quoteTotalMinor: number | null; sampleRequired: boolean; sampleStatus: "pending" | "submitted" | "approved" | "rejected" | null;
  components: { orderNumber: string; status: OrderStatus; holdReasons: string[] } | null; inbound: { number: string; status: string } | null;
  producedQuantity: number | null; billed: boolean; createdAt: string; internalNote?: string | null;
}
export interface ProductionNeed { productId: string; sku: string; name: string; role: "component" | "material"; quantity: number; available?: number; missing?: number }
export interface ProductionOrderDetail extends Omit<ProductionOrderRow, "product" | "inbound"> {
  note: string | null; bom: ProductionBomDef; bomVersion: number; product: { id: string; sku: string; name: string; bestBeforeManaged: boolean; batchManaged: boolean };
  needs: ProductionNeed[]; yields: ProductionNeed[]; componentsOrder: { id: string; orderNumber: string; status: OrderStatus; holdReasons: string[] } | null;
  inbound: { id: string; number: string; status: string; expected: number; received: number } | null;
  quote: ProductionQuoteView | null; estimate: ProductionQuoteView; plannedMinutes: number;
  sampleNote: string | null; sampleFeedback: string | null; scrapQuantity: number | null; scrapReason: string | null; batch: string | null; bestBefore: string | null; suggestedBestBefore: string | null; actualMinutes: number | null; cancelReason: string | null;
  files: ProductionFile[]; events: { at: string; action: string; message: string | null; actor: string | null }[];
  can: { cancel: boolean; accept: boolean; start: boolean; sample: boolean; decideSample: boolean; complete: boolean; editQuote: boolean; bill: boolean };
}
export interface ProductionPreview { needs: (ProductionNeed & { available: number; missing: number })[]; yields: ProductionNeed[]; quote: ProductionQuoteView; plannedMinutes: number; sampleRequired: boolean }
export interface ProductionSettings { address: Address | null; shippingMethodId: string | null; editable: boolean }
export interface ProductionCompleteInput { producedQuantity: number; scrapQuantity?: number; scrapReason?: string; batch?: string; bestBefore?: string; actualMinutes?: number; /** nur beim Auflösen: was zurück aufs Lager geht */ lines?: { productId: string; quantity: number; batch?: string; bestBefore?: string }[] }
export type { DeliveryAddress, DeliveryAddresses, PurchasingSettings, SupplierInput } from "@logiful/domain";
export type InboundAppointment = import("@logiful/domain").InboundAppointment;
export type AnnouncementAudience = import("@logiful/domain").AnnouncementAudience;
export type CalendarEvent =
  | { type: "holiday"; date: string; name: string; warehouses: string[]; allWarehouses: boolean }
  | { type: "appointment"; date: string; from: string; to: string | null; inboundId: string; number: string; status: string; note: string | null; warehouse: string | null }
  | { type: "expected"; date: string; inboundId: string; number: string; status: string; warehouse: string | null };
export interface CalendarData { events: CalendarEvent[]; sites: { name: string; region: string | null }[] }
export interface Announcement { id: string; title: string; body: string; level: "info" | "important"; pinned: boolean; publishedAt: string }
export interface AdminAnnouncement { id: string; title: string; body: string; level: "info" | "important"; pinned: boolean; audience: AnnouncementAudience; publishAt: string; visibleUntil: string | null; createdAt: string; updatedAt: string }
export interface AnnouncementInputBody { title: string; body: string; level: "info" | "important"; pinned: boolean; audience: AnnouncementAudience; publishAt: string | null; visibleUntil: string | null }
export interface WarehouseSiteItem { id: string; name: string; street: string | null; zip: string | null; city: string | null; country: string | null; manual: boolean; source: "ffn" | "manual" | "unknown"; syncedAt: string | null; region: string | null; autoRegion: string | null; effective: string | null; customers: number; code: string | null; autoCode: string | null }
export interface WarehouseSiteInput { warehouseId?: string; name?: string; street?: string | null; zip?: string | null; city?: string | null; country?: string; region?: string | null; code?: string | null }
export interface AdminAppointment { id: string; tenantId: string; tenantName: string; customerNumber: number | null; number: string; status: string; warehouseRef: string | null; note: string | null; supplier: string | null; date: string; from: string; to: string | null; lines: number; units: number }
export interface Supplier { id: string; name: string; email: string | null; contactName: string | null; phone: string | null; customerNumber: string | null; address: string | null; leadDays: number | null; language: "de" | "en"; note: string | null; products: number }
export interface PurchaseOrderItem { id: string; number: string; status: "draft" | "sent" | "cancelled"; supplierName: string; expectedAt: string | null; sentAt: string | null; createdAt: string; inboundNumber: string | null; inboundStatus: string | null; units: number; value: number | null; lines: number }
export interface PurchaseOrderDetail {
  id: string; number: string; status: PurchaseOrderItem["status"]; supplierId: string; expectedAt: string | null; note: string | null; warehouseRef: string | null; sentAt: string | null; sentTo: string | null; createdAt: string;
  supplier: Omit<Supplier, "products">;
  inboundNumber: string | null; inboundStatus: string | null;
  lines: { id: string; productId: string; sku: string; name: string; supplierSku: string | null; quantity: number; unitPrice: number | null; received: number | null }[];
}
export type { CommsSettings } from "@logiful/domain";
export interface CustomerMessage { id: string; orderId: string; kind: string; toEmail: string; status: "pending" | "sending" | "sent" | "failed" | "suppressed"; note: string | null; lastError: string | null; sentAt: string | null; sendAfter: string; createdAt: string }
export interface PublicAddress {
  shop: string;
  orderNumber: string;
  lang: "de" | "en";
  status: "open" | "closed" | "corrected" | "confirmed" | "resolved" | "expired";
  address: { company: string; firstName: string; lastName: string; street: string; houseNumber: string; addition: string; zip: string; city: string; country: string };
  lines: string[];
  /** verständliche Hinweise, was fehlt/nicht stimmt */
  issues: string[];
  /** betroffene Felder (werden markiert) */
  fields?: string[];
  contact: string | null;
  brandColor: string | null;
  footer: string | null;
  hasLogo: boolean;
}
export interface AddressRequestState {
  clarification: { status: "open" | "corrected" | "confirmed" | "resolved" | "expired"; createdAt: string; resolvedAt: string | null; expiresAt: string } | null;
  messages: { kind: string; status: CustomerMessage["status"]; sentAt: string | null; createdAt: string; note: string | null }[];
}
type PublicAddressInput = Omit<PublicAddress["address"], "country">;
type RenderedMail = { subject: string; text: string; html: string };
export type ModulePhase = "none" | "trial" | "paid" | "free" | "ended";
export interface ModulePrice { baseFee: number; unitFee: number; freeUnits: number; discountPct: number }
export interface ModulePackageInfo { id: string; name: string; description: string | null; features: string[]; bookable: boolean; archived: boolean; trialDays: number; price: ModulePrice }
export interface TenantModule {
  key: ModuleKey;
  name: string;
  description: string;
  unit: string;
  unitPlural: string;
  /** Funktionsnamen dieses Moduls (Schlüssel → Text) */
  featureLabels: Record<string, string>;
  packages: ModulePackageInfo[];
  trialAvailable: boolean;
  /** gebuchtes Paket (aktuell gültig) */
  packageId: string | null;
  /** geplanter Wechsel (Downgrade zum Monatsende) */
  pendingPackageId: string | null;
  pendingFrom: string | null;
  pendingPackageName: string | null;
  /** Preis des gebuchten Pakets für diesen Kunden */
  price: ModulePrice & { special: boolean };
  phase: ModulePhase;
  access: boolean;
  trialEndsAt: string | null;
  cancelAt: string | null;
  paidFrom: string | null;
  usageThisMonth: number;
  /** über ein Modulpaket (Bündel) gebucht */
  bundle?: { id: string; name: string } | null;
  /** nur Logiful */
  note?: string | null;
  priceOverride?: { baseFee?: string; unitFee?: string; freeUnits?: number; discountPct?: number } | null;
}
/** Modulpaket über mehrere Module (Bündel) */
export interface ModuleBundle {
  id: string;
  name: string;
  description: string | null;
  items: { moduleKey: ModuleKey; moduleName: string; packageId: string; packageName: string; features: string[]; unitFee: number; freeUnits: number; unit: string; listBaseFee: number }[];
  baseFee: number;
  /** Summe der Grundgebühren bei Einzelbuchung */
  listPrice: number;
  trialDays: number;
  bookable: boolean;
  archived: boolean;
  phase: ModulePhase;
  access: boolean;
  trialEndsAt: string | null;
  cancelAt: string | null;
  trialAvailable: boolean;
}
export interface AdminModuleBundle extends Omit<ModuleBundle, "phase" | "access" | "trialEndsAt" | "cancelAt" | "trialAvailable"> {
  sortOrder: number;
  subscriptions: { tenantId: string; tenantName: string; customerNumber: string | null; phase: ModulePhase; trialEndsAt: string | null; cancelAt: string | null }[];
}
export interface ModuleBundleInput { name: string; description: string | null; items: { moduleKey: string; packageId: string }[]; baseFee: string; trialDays: number; bookable: boolean; archived?: boolean }
export type AdminBundleAction = { action: "trial"; days: number } | { action: "free" | "paid" | "end_now" | "end_at_month_end" | "undo_cancel" };

export interface ModulePackageInput { name: string; description: string | null; features: string[]; bookable: boolean; trialDays: number; baseFee: string; unitFee: string; freeUnits: number; billingBaseArticleId: number | null; billingUnitArticleId: number | null; sortOrder?: number; archived?: boolean }
export interface AdminModulePackage extends ModulePackageInfo { sortOrder: number; baseFee: string; unitFee: string; freeUnits: number; billingBaseArticleId: number | null; billingUnitArticleId: number | null }
export interface AdminModuleOverview {
  key: ModuleKey;
  name: string;
  description: string;
  unit: string;
  unitPlural: string;
  featureLabels: Record<string, string>;
  packages: AdminModulePackage[];
  counts: { trial: number; paid: number; free: number };
  subscriptions: { tenantId: string; tenantName: string; customerNumber: string | null; phase: ModulePhase; packageId: string | null; packageName: string | null; pendingPackageName: string | null; pendingFrom: string | null; trialEndsAt: string | null; cancelAt: string | null; special: boolean; note: string | null }[];
}
export type AdminModuleAction =
  | { action: "trial"; days: number; packageId?: string }
  | { action: "free"; packageId?: string }
  | { action: "paid"; packageId?: string }
  | { action: "package"; packageId: string }
  | { action: "end_now" }
  | { action: "end_at_month_end" }
  | { action: "undo_cancel" }
  | { action: "price"; override: { baseFee?: string; unitFee?: string; freeUnits?: number; discountPct?: number } | null }
  | { action: "note"; note: string | null };

export interface ReturnClarification {
  id: string;
  number: string | null;
  reasons: string[];
  warehouseOrderNumber: string | null;
  shopOrderNumber: string | null;
  trackingNumber: string | null;
  location: string | null;
  note: string | null;
  hasDocument: boolean;
  returnCaseId: string | null;
  orderId: string | null;
  orderNumber?: string | null;
  reportedAt: string | null;
}

export interface ReturnSource {
  id: string;
  name: string;
  kind: "retool" | "returns-portal" | "operations";
  status: string;
  lastError: string | null;
  lastCheckedAt: string | null;
  lastSyncAt: string | null;
  linkedTenants: ReturnLinkedTenant[];
}
export interface ReturnCustomer { firstName?: string; lastName?: string; company?: string; email?: string; zip?: string; city?: string; country?: string }
export type ShopifyReturnState = "pending" | "done" | "skipped" | "error";
export interface ReturnCaseItem {
  id: string;
  orderId: string | null;
  /** Meldung an Shopify über Connect (null = noch nicht gemeldet) */
  shopify?: { state: ShopifyReturnState } | null;
  shopOrderNumber: string | null;
  warehouseOrderNumber: string | null;
  customer: ReturnCustomer | null;
  status: ReturnStatus;
  statusLabel: string;
  undeliverable: boolean;
  units: number;
  conditions: Record<string, number>;
  processedAt: string | null;
  notifiedAt: string | null;
}
export interface ReturnLineItem { id: string; productId: string | null; sku: string | null; name: string | null; quantity: number; condition: string | null; undeliverable: boolean; undeliverableReason: string | null; conditionReasons?: string[]; status: string | null; location: string | null; processedAt: string | null }
export interface ReturnCaseDetail extends Omit<ReturnCaseItem, "orderId" | "units" | "conditions" | "shopify"> {
  order: { id: string; orderNumber: string; status: string; shopify?: boolean } | null;
  clarifications?: ReturnClarification[];
  lines: ReturnLineItem[];
  /** Meldung an Shopify über Connect */
  shopify: { state: "pending" | "done" | "skipped" | "error"; message: string | null; at: string | null; refunded: boolean } | null;
}
export interface ReturnsList {
  configured: boolean;
  page: number;
  pageSize: number;
  total: number;
  stats: { week: number; month: number; undeliverable30: number; failed: number };
  /** Treffer je Filter (jeweils unter den anderen aktiven Filtern) */
  facets?: Record<"all" | ReturnStatus | "A" | "B" | "C" | "undeliverable", number>;
  items: ReturnCaseItem[];
}
/** Mandant im Retourenportal (Übersicht im Logiful-Admin) */
export interface ReturnsPortalTenant { tenantId: string; slug: string; name: string; active: boolean; connectTenantId: string | null; customerNumber?: string | null; setupDone: boolean; openReturns: number; returns30d: number }

/** Retourenportal (eigener Dienst) des Kunden */
export interface ReturnsPortalInfo {
  configured: boolean;
  reachable: boolean;
  status: { tenantId: string; slug: string; active: boolean; setupDone: boolean; openReturns: number } | null;
}

export interface OrderReturn { id: string; status: ReturnStatus; statusLabel: string; undeliverable: boolean; processedAt: string | null; shopify: { state: "pending" | "done" | "skipped" | "error"; message: string | null } | null; lines: { sku: string | null; name: string | null; quantity: number; condition: string | null; undeliverableReason: string | null; conditionReasons?: string[] }[] }

/** Störung (Monitoring) */
export interface CheckResult { key: string; label: string; status: "ok" | "warning" | "critical"; detail: string }
export interface SystemStatus {
  lastRun: { at: string; results: CheckResult[] } | null;
  heartbeat: { at: string; host: string; startedAt: string; ageSec: number } | null;
  queues: { key: string; label: string; waiting: number; active: number; delayed: number; failed: number; error: string | null }[];
  channels: { emails: string[]; webhook: { configured: boolean; host: string | null } };
}
export interface FailedJob { queue: string; queueLabel: string; id: string; name: string; failedReason: string; attemptsMade: number; finishedOn: string | null; data: string }
export interface SystemAlert { id: string; key: string; severity: "warning" | "critical"; title: string; detail: string | null; count: number; firstAt: string; lastAt: string; resolvedAt: string | null; notifiedAt: string | null }

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  /** Kundenkürzel, z. B. in Wareneingangsnummern (WE-NORDLI-2026-0007) */
  code?: string | null;
  createdAt: string;
  members: number;
  /** Artikel automatisch für Logiful freigeben (nur mit Zustimmung des Kunden) */
  autoReleaseProducts?: boolean;
  /** Testbetrieb (Hardlock) seit */
  testModeSince?: string | null;
  /** Logiful-Kundennummer – Schlüssel über alle Systeme */
  customerNumber?: string | null;
  /** Interessenten-Ansicht: Ausnahmen je Interessent */
  prospectAccess?: Record<string, string> | null;
  /** Warnung bei überfälligen Rechnungen: Ausnahme je Kunde */
  overdueWarning?: import("@logiful/domain").OverdueOverride | null;
  /** offene Rechnungen (letzter Abgleich) */
  openInvoices?: import("@logiful/domain").OpenInvoicesSnapshot | null;
  /** Selbst-Registrierung (Web/Shopify-App) mit Angaben fürs Angebot */
  signup?: { source: "web" | "shopify"; shop?: string; contactName: string; email: string; phone?: string; at: string; offer?: import("@logiful/domain").SignupOffer } | null;
  /** Verweise in anderen Systemen (jtl-wawi, jtl-ffn, retool-returns, returns-portal) */
  refs?: Record<string, string>;
}

/** Sales Room (Kundenseite des Retool-„Sales Deal Manager“) */
export interface SalesRoom {
  language: "de" | "en";
  deal: { title: string | null; company: string | null; contactName: string | null; customerLogoUrl: string | null; packagesPerMonth: number | null };
  blocks: Record<import("@logiful/domain").SalesRoomBlock, boolean>;
  welcome: Record<"de" | "en", import("@logiful/domain").SalesWelcomeText>;
  offer: { title: string | null; date: string; groups: Record<"de" | "en", import("@logiful/domain").SalesOfferGroup[]> } | null;
  calculator: { pick: number; pack: number };
  faqs: Record<"de" | "en", import("@logiful/domain").SalesFaqItem[]> | null;
  presentation: boolean;
}

/** Umzug offener Bestellungen von FFN Connect (Logiful intern) */
export interface ChannelMigration {
  id: string;
  status: "previewing" | "preview" | "running" | "done" | "stopped" | "failed";
  error: string | null;
  fromLocationId: string | null;
  fromLocationName: string | null;
  locations: { id: string; name: string; count: number }[];
  /** erkanntes Präfix der Lageraufträge von FFN Connect, z. B. „4171“ */
  outboundPrefix: string | null;
  items: { fulfillmentOrderId: string; orderId: string; orderName: string; orderCreatedAt: string; outboundRef: string | null; outboundStatus: string | null; step: "waiting" | "cancel_requested" | "requested" | "done" | "kept" | "failed"; message: string | null; locationId?: string | null; locationName?: string; at: string }[];
  pauseUntil: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface FfnMerchants {
  /** OAuth-Client (FFN_CLIENT_ID/SECRET) bei Render hinterlegt */
  clientConfigured: boolean;
  /** Logiful-Fulfiller-Konto per OAuth verbunden */
  fulfillerConnected: boolean;
  connectedAt: string | null;
  /** FFN-ID des Logiful-Kontos (aus /users/current beim Verbinden) */
  fulfillerUserId: string | null;
  error?: string;
  merchants: { merchantId: string; company: string; city: string | null; email: string | null; active: boolean; portal: { tenantId: string; tenantName: string; status: string; authorized: boolean } | null }[];
}

/** FFN-Stand je Kunde (Logiful-Sicht) */
export interface FfnLink {
  tenantId: string;
  status: "pending" | "active" | "error" | "disconnected";
  authorized: boolean;
  merchantId: string | null;
  merchantCompany: string | null;
  warehouseName: string | null;
  lastError: string | null;
}

export interface StaffMember {
  userId: string;
  email: string;
  name: string | null;
  role: StaffRole | null;
  lastLoginAt: string | null;
  disabledAt: string | null;
}

// ── DHL-Adressprüfung ────────────────────────────────────────────────
export type ShippingAddress = OrderDetail["shippingAddress"];
export interface DhlVersion {
  version: string;
  status: "importing" | "active" | "superseded" | "failed";
  stats: Record<string, number>;
  progress: { step: string; done: number; total: number } | null;
  error: string | null;
  createdAt: string;
  activatedAt: string | null;
}
export interface DhlStatus { active: DhlVersion | null; latest: DhlVersion | null; versions: DhlVersion[] }
export interface AddressCase {
  id: string;
  tenantId: string;
  tenantName: string;
  channelName: string;
  orderNumber: string;
  placedAt: string;
  shippingAddress: ShippingAddress;
  check: AddressCheckResult & { mode: AddressCheckSettings["mode"]; checkedAt: string };
}
export interface AddressTenant { id: string; name: string; settings: AddressCheckSettings; openCases: number }
export type { AddressCheckResult, AddressCheckSettings };

/** Darf diese Rolle Shops, Regeln und Team verwalten? */
export const canManage = (role: EffectiveRole | undefined) => role === "staff_admin" || role === "staff" || role === "customer_admin";

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: unknown) {
    super(`API ${status}`);
    // Fehlermeldungen des Servers sind deutsche Sätze – für die Anzeige in der gewählten Sprache
    const b = body as { message?: unknown; error?: unknown } | null;
    if (b && typeof b === "object") {
      if (typeof b.message === "string") b.message = serverText(b.message);
      if (typeof b.error === "string") b.error = serverText(b.error);
    }
  }
}

async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) throw new ApiError(res.status, json);
  return json as T;
}

const qs = (q: object) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

// ── Datenschutz-Anfragen (Shopify-Pflicht-Webhooks) ────────────────────
export interface PrivacyRequest {
  id: string;
  kind: "data_request" | "customer_redact" | "shop_redact";
  status: "received" | "ready" | "forwarded" | "done" | "failed";
  customerEmail: string | null;
  orders: number;
  dueAt: string;
  completedAt: string | null;
  createdAt: string;
}

// ── Tracking-Seite ────────────────────────────────────────────────────
export type TrackingDomainStatus = "none" | "dns_pending" | "provisioning" | "active" | "error";
export interface TrackingPageAdmin {
  slug: string;
  draft: import("@logiful/domain").TrackingPageConfig;
  published: boolean;
  publishedAt: string | null;
  /** Entwurf weicht von der veröffentlichten Fassung ab */
  changed: boolean;
  linkInShop: boolean;
  searchEnabled: boolean;
  publicUrl: string;
  defaultUrl: string;
  domain: { name: string | null; status: TrackingDomainStatus; message: string | null; checkedAt: string | null; cnameTarget: string; automatic: boolean };
  features: { customDomain: boolean };
  shop: string;
  assetCount: number;
}
export interface TrackingPageInput {
  draft: import("@logiful/domain").TrackingPageConfig;
  linkInShop: boolean;
  searchEnabled: boolean;
  slug: string;
}

// ── Partner Network ───────────────────────────────────────────────────
export interface PartnerCard {
  id: string; name: string; tagline: string; description: string; category: string; website: string | null;
  size: "s" | "m" | "l"; accent: string; benefit: string; discountCode: string; mode: "lead" | "link";
  logoUrl: string | null; coverUrl: string | null;
  /** letzte eigene Anfrage */
  lastRequestAt?: string;
}
export interface AdminPartner extends PartnerCard {
  linkUrl: string | null; leadEmail: string | null; commission: PartnerCommission; audience: PartnerAudience; active: boolean; sortOrder: number;
  createdAt: string; updatedAt: string;
  stats: { leads: number; won: number; commissionEur: number; clicks: number };
}
export type PartnerSaveInput = Omit<AdminPartner, "id" | "logoUrl" | "coverUrl" | "lastRequestAt" | "createdAt" | "updatedAt" | "stats">;
export interface PartnerLeadRow {
  id: string; partnerId: string; partnerName: string; tenantId: string; tenantName: string; customerNumber: string | null; userEmail: string | null;
  message: string; contact: { name: string; email: string; phone: string; company: string };
  status: "new" | "contacted" | "won" | "lost"; note: string;
  leadFeeEur: number | null; percent: number | null; dealValueEur: number | null; commissionEur: number;
  notified: boolean; createdAt: string;
}

const httpApi = {
  partners: {
    list: (t: string) => http<PartnerCard[]>("GET", `/api/tenants/${t}/partners`),
    request: (t: string, id: string, body: { message: string; contact: { name: string; email: string; phone: string; company: string }; consent: true }) => http<{ ok: true }>("POST", `/api/tenants/${t}/partners/${id}/leads`, body),
    click: (t: string, id: string) => http<{ url: string }>("POST", `/api/tenants/${t}/partners/${id}/click`),
    adminList: () => http<AdminPartner[]>("GET", "/api/admin/partners"),
    create: (b: PartnerSaveInput) => http<AdminPartner>("POST", "/api/admin/partners", b),
    update: (id: string, b: PartnerSaveInput) => http<AdminPartner>("PUT", `/api/admin/partners/${id}`, b),
    remove: (id: string) => http<void>("DELETE", `/api/admin/partners/${id}`),
    setImage: (id: string, kind: "logo" | "cover", b: { mimeType?: string; data?: string }) => http<{ ok: true }>("PUT", `/api/admin/partners/${id}/image/${kind}`, b),
    leads: () => http<PartnerLeadRow[]>("GET", "/api/admin/partner-leads"),
    updateLead: (id: string, b: { status?: PartnerLeadRow["status"]; dealValueEur?: number | null; note?: string }) => http<{ ok: true; commissionEur: number }>("PUT", `/api/admin/partner-leads/${id}`, b),
  },
  me: () => http<Me>("GET", "/api/me"),
  /** Rundgang gesehen/übersprungen */
  onboardingDone: (tour: string, version: number) => http<Record<string, number>>("POST", "/api/me/onboarding", { tour, version }),
  env: () => http<{ env: "production" | "staging" }>("GET", "/api/env"),
  purchasing: {
    suppliers: (t: string) => http<Supplier[]>("GET", `/api/tenants/${t}/suppliers`),
    createSupplier: (t: string, b: import("@logiful/domain").SupplierInput) => http<Supplier>("POST", `/api/tenants/${t}/suppliers`, b),
    updateSupplier: (t: string, id: string, b: import("@logiful/domain").SupplierInput) => http<{ ok: true }>("PUT", `/api/tenants/${t}/suppliers/${id}`, b),
    archiveSupplier: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/suppliers/${id}`),
    settings: (t: string) => http<{ settings: import("@logiful/domain").PurchasingSettings; defaultCompanyName: string }>("GET", `/api/tenants/${t}/purchasing/settings`),
    saveSettings: (t: string, b: import("@logiful/domain").PurchasingSettings) => http<{ settings: import("@logiful/domain").PurchasingSettings }>("PUT", `/api/tenants/${t}/purchasing/settings`, b),
    orders: (t: string) => http<PurchaseOrderItem[]>("GET", `/api/tenants/${t}/purchase-orders`),
    ordersPage: (t: string, q: { page: number; pageSize?: 50 | 100 | 250 }) => http<Paged<PurchaseOrderItem>>("GET", `/api/tenants/${t}/purchase-orders${qs(q)}`),
    order: (t: string, id: string) => http<PurchaseOrderDetail>("GET", `/api/tenants/${t}/purchase-orders/${id}`),
    createDrafts: (t: string, b: { items: { productId: string; quantity: number }[]; supplierId?: string | null }) => http<{ created: { id: string; number: string; supplierId: string }[] }>("POST", `/api/tenants/${t}/purchase-orders`, b),
    updateOrder: (t: string, id: string, b: { supplierId: string; expectedAt: string | null; note: string | null; warehouseRef: string | null; lines: { productId: string; quantity: number; unitPrice: number | null }[] }) => http<{ ok: true }>("PUT", `/api/tenants/${t}/purchase-orders/${id}`, b),
    sendOrder: (t: string, id: string) => http<{ ok: true; sentTo: string; inboundNumber: string; testMode: boolean }>("POST", `/api/tenants/${t}/purchase-orders/${id}/send`),
    cancelOrder: (t: string, id: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/purchase-orders/${id}/cancel`),
    pdfUrl: (t: string, id: string) => `/api/tenants/${t}/purchase-orders/${id}/pdf`,
  },
  customerComms: {
    get: (t: string) => http<{ settings: import("@logiful/domain").CommsSettings; defaultSenderName: string; masterEnabled: boolean; hasLogo: boolean }>("GET", `/api/tenants/${t}/customer-comms`),
    logoUrl: (t: string) => `/api/tenants/${t}/customer-comms/logo`,
    uploadLogo: (t: string, data: string) => http<{ ok: true }>("PUT", `/api/tenants/${t}/customer-comms/logo`, { data }),
    deleteLogo: (t: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/customer-comms/logo`),
    save: (t: string, settings: import("@logiful/domain").CommsSettings) => http<{ settings: import("@logiful/domain").CommsSettings }>("PUT", `/api/tenants/${t}/customer-comms`, settings),
    preview: (t: string, settings: import("@logiful/domain").CommsSettings) => http<{ address: RenderedMail; delay: RenderedMail }>("POST", `/api/tenants/${t}/customer-comms/preview`, { settings }),
    test: (t: string, kind: "address" | "delay") => http<{ sentTo: string }>("POST", `/api/tenants/${t}/customer-comms/test`, { kind }),
    messages: (t: string) => http<CustomerMessage[]>("GET", `/api/tenants/${t}/customer-comms/messages`),
  },
  privacy: {
    list: (t: string) => http<PrivacyRequest[]>("GET", `/api/tenants/${t}/privacy-requests`),
    exportUrl: (t: string, id: string) => `/api/tenants/${t}/privacy-requests/${id}/export`,
    forwarded: (t: string, id: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/privacy-requests/${id}/forwarded`),
  },
  trackingPage: {
    get: (t: string) => http<TrackingPageAdmin>("GET", `/api/tenants/${t}/tracking-page`),
    save: (t: string, b: TrackingPageInput) => http<TrackingPageAdmin>("PUT", `/api/tenants/${t}/tracking-page`, b),
    publish: (t: string) => http<TrackingPageAdmin>("POST", `/api/tenants/${t}/tracking-page/publish`),
    unpublish: (t: string) => http<TrackingPageAdmin>("POST", `/api/tenants/${t}/tracking-page/unpublish`),
    /** Bild als Base64 (ohne data:-Präfix) */
    uploadAsset: (t: string, data: string) => http<{ id: string }>("POST", `/api/tenants/${t}/tracking-page/assets`, { data }),
    assetUrl: (id: string) => `/public/tracking-assets/${id}`,
    sample: (t: string) => http<{ order: import("@logiful/domain").TrackingOrderView | null; token?: string }>("GET", `/api/tenants/${t}/tracking-page/sample`),
    setDomain: (t: string, domain: string | null) => http<TrackingPageAdmin>("PUT", `/api/tenants/${t}/tracking-page/domain`, { domain }),
    checkDomain: (t: string) => http<TrackingPageAdmin>("POST", `/api/tenants/${t}/tracking-page/domain/check`),
    activateDomain: (t: string) => http<TrackingPageAdmin>("POST", `/api/admin/tracking-pages/${t}/domain/activate`),
  },
  /** öffentliche Tracking-Seite (Endkunden) – slug null = eigene Domain (Host) */
  publicTracking: {
    site: (slug: string | null) => http<import("@logiful/domain").TrackingSite>("GET", slug ? `/public/tracking/${encodeURIComponent(slug)}` : "/public/tracking/host"),
    order: (slug: string, token: string) => http<{ site: import("@logiful/domain").TrackingSite; order: import("@logiful/domain").TrackingOrderView }>("GET", `/public/tracking/${encodeURIComponent(slug)}/order/${encodeURIComponent(token)}`),
    lookup: (slug: string, orderNumber: string, contact: string) => http<{ token: string }>("POST", `/public/tracking/${encodeURIComponent(slug)}/lookup`, { orderNumber, contact }),
  },
  /** Offene Rechnungen (Banner „überfällige Rechnungen“) */
  invoices: {
    open: (t: string) => http<{ checkedAt: string | null; afterDays: number; invoices: { id: number; number: string | null; date: string; gross: number; open: number; days: number; paymentMethod: string | null; debitPending: boolean; overdue: boolean }[] }>("GET", `/api/tenants/${t}/invoices/open`),
  },
  /** Sales Room (Link aus der Angebotsmail, ohne Anmeldung) – Inhalte aus dem Retool-„Sales Deal Manager“ */
  salesRoom: {
    get: (token: string) => http<SalesRoom>("GET", `/public/sales-room/${encodeURIComponent(token)}`),
    pdfUrl: (token: string, lang: "de" | "en") => `/public/sales-room/${encodeURIComponent(token)}/offer.pdf?lang=${lang}`,
    opened: (token: string, et: string) => http<void>("POST", `/public/sales-room/${encodeURIComponent(token)}/opened`, { et }),
  },
  /** Selbst-Registrierung neuer Kunden (Website) */
  register: (b: import("@/components/SignupForm").SignupData) => http<{ ok: true }>("POST", "/api/register", b),
  /** Shop aus der Logiful-App in Shopify verbinden (Verknüpfungscode) */
  shopifyClaim: {
    get: (claim: string) => http<{ shop: string }>("GET", `/api/shopify/claim/${encodeURIComponent(claim)}`),
    register: (b: { claim: string } & import("@/components/SignupForm").SignupData) => http<{ ok: true }>("POST", "/api/shopify/claim/register", b),
    link: (claim: string, tenantId: string) => http<{ channelId: string; shop: string }>("POST", "/api/shopify/claim/link", { claim, tenantId }),
  },
  publicAddress: {
    get: (token: string) => http<PublicAddress>("GET", `/public/address/${encodeURIComponent(token)}`),
    logoUrl: (token: string) => `/public/address/${encodeURIComponent(token)}/logo`,
    submit: (token: string, b: { action: "check" | "confirm"; address: PublicAddressInput }) => http<{ result: "saved" } | { result: "suggestion"; suggestion: Partial<PublicAddressInput> } | { result: "unclear"; issues: string[]; fields?: string[] }>("POST", `/public/address/${encodeURIComponent(token)}`, b),
  },
  auth: {
    requestLink: (email: string) => http<{ ok: true }>("POST", "/auth/login", { email }),
    verify: (token: string) => http<{ ok: true; mfa?: "ok" | "pending" | "setup" }>("POST", "/auth/verify", { token }),
    mfa: (code: string) => http<{ ok: true; usedRecovery?: boolean }>("POST", "/auth/mfa", { code }),
    logout: () => http<{ ok: true }>("POST", "/auth/logout"),
    updateProfile: (name: string) => http<{ ok: true }>("PATCH", "/api/me", { name }),
    setLocale: (locale: "de" | "en") => http<{ ok: true }>("PATCH", "/api/me", { locale }),
  },
  backend: {
    get: (t: string) => http<BackendState>("GET", `/api/tenants/${t}/backend`),
    warehouses: (t: string) => http<{ warehouseId: string; name: string; city: string | null; country: string | null }[]>("GET", `/api/tenants/${t}/backend/warehouses`),
    selectWarehouse: (t: string, warehouseId: string) => http<{ ok: true }>("PUT", `/api/tenants/${t}/backend/warehouse`, { warehouseId }),
    setWarehouses: (t: string, body: WarehouseSettings) => http<{ ok: true }>("PUT", `/api/tenants/${t}/backend/warehouses`, body),
    sync: (t: string) => http<{ queued: true }>("POST", `/api/tenants/${t}/backend/sync`),
    importProducts: (t: string) => http<{ runId: string }>("POST", `/api/tenants/${t}/backend/import-products`),
    importOrders: (t: string, body: { period: "30" | "90" | "180" | "365" | "all"; withShipments: boolean }) => http<{ runId: string }>("POST", `/api/tenants/${t}/backend/import-orders`, body),
    shippingMethods: (t: string) => http<ShippingMethodsState>("GET", `/api/tenants/${t}/backend/shipping-methods`),
    /** Versandarten von FFN abrufen (z. B. nach Anlage einer eigenen Versandart beim Kunden) */
    syncShippingMethods: (t: string) => http<ShippingMethodsState & { added: string[]; removed: string[] }>("POST", `/api/tenants/${t}/backend/shipping-methods/sync`),
    disconnect: (t: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/backend`),
    /** Browser-Weiterleitung zum JTL-Login (OAuth) */
    connectUrl: (t: string) => `/ffn/connect?tenantId=${encodeURIComponent(t)}`,
  },
  team: {
    get: (t: string) => http<TeamState>("GET", `/api/tenants/${t}/members`),
    invite: (t: string, email: string, role: TenantRole, permissions?: StoredPermissions) => http<{ ok: true }>("POST", `/api/tenants/${t}/invites`, { email, role, ...(permissions ? { permissions } : {}) }),
    update: (t: string, userId: string, body: { role?: TenantRole; permissions?: StoredPermissions | null }) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/members/${userId}`, body),
    revokeInvite: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/invites/${id}`),
    setRole: (t: string, userId: string, role: TenantRole) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/members/${userId}`, { role }),
    remove: (t: string, userId: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/members/${userId}`),
  },
  mcp: {
    connections: (t: string) => http<McpConnections>("GET", `/api/tenants/${t}/mcp/connections`),
    revoke: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/mcp/connections/${id}`),
    adminTools: () => http<{ switches: { name: string; label: string; sensitive: boolean }[]; values: McpToolSwitches; tenants: { id: string; name: string }[] }>("GET", "/api/admin/mcp/tools"),
    saveAdminTools: (v: McpToolSwitches) => http<McpToolSwitches>("PUT", "/api/admin/mcp/tools", v),
    request: (id: string) => http<{ client: string; redirectHost: string; scope: string; tenants: { id: string; name: string; eligible: boolean }[] }>("GET", `/api/oauth/requests/${id}`),
    decide: (id: string, b: { approve: boolean; tenantId?: string }) => http<{ redirect: string }>("POST", `/api/oauth/requests/${id}/decision`, b),
  },
  fba: {
    product: (t: string, id: string) => http<{ fnsku: string | null; labelRequired: boolean; asin: string | null }>("GET", `/api/tenants/${t}/products/${id}/fba`),
    saveProduct: (t: string, id: string, b: { fnsku: string | null; labelRequired: boolean }) => http<{ fnsku: string | null; labelRequired: boolean }>("PUT", `/api/tenants/${t}/products/${id}/fba`, b),
    shipments: (t: string, q: { page?: number; pageSize?: number } = {}) => {
      const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]));
      return http<{ total: number; page: number; pageSize: number; items: FbaShipmentRow[] }>("GET", `/api/tenants/${t}/fba/shipments${p.size ? `?${p}` : ""}`);
    },
    create: (t: string, b: FbaShipmentCreate) => http<{ id: string; orderNumber: string }>("POST", `/api/tenants/${t}/fba/shipments`, b),
    setShipmentId: (t: string, id: string, b: { shipmentId: string; referenceId?: string }) => http<FbaOrderInfo>("PATCH", `/api/tenants/${t}/fba/shipments/${id}`, b),
    documents: (t: string, id: string) => http<{ documents: FbaDocument[]; missing: FbaDocumentKind[] }>("GET", `/api/tenants/${t}/fba/shipments/${id}/documents`),
    upload: (t: string, id: string, b: { kind: FbaDocumentKind; filename: string; data: string }) => http<{ id: string; missing: FbaDocumentKind[] }>("POST", `/api/tenants/${t}/fba/shipments/${id}/documents`, b),
    deleteDocument: (t: string, id: string, docId: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/fba/shipments/${id}/documents/${docId}`),
    documentUrl: (t: string, id: string, docId: string) => `/api/tenants/${t}/fba/shipments/${id}/documents/${docId}`,
    saveCartons: (t: string, id: string, cartons: FbaCarton[]) => http<FbaOrderInfo>("PUT", `/api/tenants/${t}/fba/shipments/${id}/cartons`, { cartons }),
    cartonsCsvUrl: (t: string, id: string) => `/api/tenants/${t}/fba/shipments/${id}/cartons.csv`,
  },
  b2b: {
    customers: (t: string, archived = false) => http<B2bCustomer[]>("GET", `/api/tenants/${t}/b2b/customers${archived ? "?archived=true" : ""}`),
    createCustomer: (t: string, c: B2bCustomerInput) => http<B2bCustomer>("POST", `/api/tenants/${t}/b2b/customers`, c),
    updateCustomer: (t: string, id: string, c: B2bCustomerInput) => http<B2bCustomer>("PATCH", `/api/tenants/${t}/b2b/customers/${id}`, c),
    archiveCustomer: (t: string, id: string, archived: boolean) => http<B2bCustomer>("POST", `/api/tenants/${t}/b2b/customers/${id}/archive`, { archived }),
    orders: (t: string, q: { page?: number; pageSize?: number; customerId?: string } = {}) => {
      const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]));
      return http<{ total: number; page: number; pageSize: number; items: B2bOrderRow[] }>("GET", `/api/tenants/${t}/b2b/orders${p.size ? `?${p}` : ""}`);
    },
    createOrder: (t: string, o: B2bOrderCreate) => http<{ id: string; orderNumber: string }>("POST", `/api/tenants/${t}/b2b/orders`, o),
    lots: (t: string, productId: string) => http<{ batch: string; bestBefore: string | null; available: number }[]>("GET", `/api/tenants/${t}/b2b/lots${qs({ productId })}`),
    deliveryNoteUrl: (t: string, id: string) => `/api/tenants/${t}/b2b/orders/${id}/delivery-note`,
  },
  dev: {
    find: (t: string, q: { order?: string; inbound?: string; sku?: string }) => http<{ orderId: string | null; inboundId: string | null; productId: string | null }>("GET", `/api/admin/dev/tenants/${t}/find${qs(q)}`),
    order: (t: string, id: string) => http<DevOrder>("GET", `/api/admin/dev/tenants/${t}/orders/${id}`),
    ship: (t: string, id: string, b: { carrier: string; trackingNumber?: string; report: boolean; lines?: DevShipLine[]; confirmLive?: boolean }) => http<{ queued: true; trackingNumber: string }>("POST", `/api/admin/dev/tenants/${t}/orders/${id}/ship`, b),
    delivery: (t: string, id: string, b: { status: string; confirmLive?: boolean }) => http<{ packages: number }>("POST", `/api/admin/dev/tenants/${t}/orders/${id}/delivery`, b),
    returnCase: (t: string, id: string, b: { condition: string; pushShopify: boolean; confirmLive?: boolean }) => http<{ caseId: string; units: number }>("POST", `/api/admin/dev/tenants/${t}/orders/${id}/return`, b),
    receive: (t: string, id: string, share: "full" | "partial") => http<{ ok: true }>("POST", `/api/admin/dev/tenants/${t}/inbounds/${id}/receive`, { share }),
    stock: (t: string, id: string, b: { available: number; batch?: string; bestBefore?: string; confirmLive?: boolean }) => http<{ queued: true }>("POST", `/api/admin/dev/tenants/${t}/products/${id}/stock`, b),
    resetShipment: (t: string, id: string) => http<{ removed: number }>("POST", `/api/admin/dev/tenants/${t}/orders/${id}/reset-shipment`),
    updateShipment: (t: string, id: string, shipmentId: string, lines: DevShipLine[]) => http<{ ok: true }>("PUT", `/api/admin/dev/tenants/${t}/orders/${id}/shipments/${shipmentId}`, { lines }),
    sync: (t: string, what: "channel" | "tracking" | "webhooks" | "orders") => http<{ queued: number }>("POST", `/api/admin/dev/tenants/${t}/sync`, { what }),
  },
  admin: {
    tenants: () => http<AdminTenant[]>("GET", "/api/admin/tenants"),
    /** Umzug von FFN Connect */
    channelMigration: (t: string, c: string) => http<{ migration: ChannelMigration | null }>("GET", `/api/admin/tenants/${t}/channels/${c}/migration`),
    channelMigrationPreview: (t: string, c: string) => http<{ migration: ChannelMigration }>("POST", `/api/admin/tenants/${t}/channels/${c}/migration/preview`),
    channelMigrationStart: (t: string, c: string, locationId: string, fulfillmentOrderIds?: string[]) => http<{ migration: ChannelMigration }>("POST", `/api/admin/tenants/${t}/channels/${c}/migration/start`, { locationId, ...(fulfillmentOrderIds ? { fulfillmentOrderIds } : {}) }),
    channelMigrationStop: (t: string, c: string) => http<{ migration: ChannelMigration }>("POST", `/api/admin/tenants/${t}/channels/${c}/migration/stop`),
    /** Warnung bei überfälligen Rechnungen */
    overdueWarning: () => http<{ defaults: import("@logiful/domain").OverdueWarningSettings }>("GET", "/api/admin/overdue-warning"),
    saveOverdueWarning: (v: import("@logiful/domain").OverdueWarningSettings) => http<{ defaults: import("@logiful/domain").OverdueWarningSettings }>("PUT", "/api/admin/overdue-warning", v),
    saveTenantOverdueWarning: (tenantId: string, override: import("@logiful/domain").OverdueOverride | null) => http<{ override: unknown }>("PUT", `/api/admin/tenants/${tenantId}/overdue-warning`, { override }),
    refreshOpenInvoices: () => http<{ queued: true }>("POST", "/api/admin/overdue-warning/refresh"),
    /** Interessenten-Ansicht: Standard und Ausnahmen je Interessent */
    prospectAccess: () => http<{ defaults: import("@logiful/domain").ProspectAccess }>("GET", "/api/admin/prospect-access"),
    saveProspectAccess: (v: import("@logiful/domain").ProspectAccess) => http<{ defaults: import("@logiful/domain").ProspectAccess }>("PUT", "/api/admin/prospect-access", v),
    saveTenantProspectAccess: (tenantId: string, access: import("@logiful/domain").ProspectAccess | null) => http<{ override: Record<string, string> | null }>("PUT", `/api/admin/tenants/${tenantId}/prospect-access`, { access }),
    loadTest: () => http<{ exists: boolean; tenantId?: string; createdAt?: string; products?: number; orders?: number; orderLines?: number; inbounds?: number }>("GET", "/api/admin/load-test"),
    seedLoadTest: (body: { products: number; orders: number; inbounds: number }) => http<{ queued: boolean }>("POST", "/api/admin/load-test", body),
    deleteLoadTest: () => http<{ queued: boolean }>("DELETE", "/api/admin/load-test"),
    loadRun: () => http<LoadRunReport>("GET", "/api/admin/load-test/run"),
    startLoadRun: (body: { orders: number; minutes: number; latencyMs: number; shipMinutes: number }) => http<{ runId: string }>("POST", "/api/admin/load-test/run", body),
    stopLoadRun: () => http<{ ok: true }>("POST", "/api/admin/load-test/run/stop"),
    setTenantCode: (t: string, code: string) => http<{ code: string }>("PUT", `/api/admin/tenants/${t}/code`, { code }),
    nextCustomerNumber: () => http<{ next: string }>("GET", "/api/admin/customer-numbers/next"),
    setCustomerNumber: (t: string, customerNumber?: string) => http<{ customerNumber: string }>("PUT", "/api/admin/tenants/" + t + "/customer-number", customerNumber ? { customerNumber } : {}),
    setTestMode: (t: string, enabled: boolean) => http<{ enabled: boolean; since: string | null }>("PUT", `/api/admin/tenants/${t}/test-mode`, { enabled }),
    setAutoRelease: (t: string, enabled: boolean) => http<{ enabled: boolean }>("PUT", `/api/admin/tenants/${t}/auto-release`, { enabled }),
    createTenant: (body: { name: string; slug: string; adminEmail?: string; shipsOutsideEu?: boolean; customerNumber?: string }) => http<{ id: string; inviteSent: boolean }>("POST", "/api/admin/tenants", body),
    staff: () => http<StaffMember[]>("GET", "/api/admin/staff"),
    ffnMerchants: () => http<FfnMerchants>("GET", "/api/admin/ffn"),
    disconnectFulfiller: () => http<{ ok: true }>("DELETE", "/api/admin/ffn"),
    fulfillerConnectUrl: "/ffn/fulfiller/connect",
    ffnLinks: () => http<FfnLink[]>("GET", "/api/admin/ffn/links"),
    assignMerchant: (t: string, merchantId: string, merchantCompany?: string) => http<{ ok: true }>("PUT", `/api/admin/tenants/${t}/ffn`, { merchantId, ...(merchantCompany ? { merchantCompany } : {}) }),
    unassignMerchant: (t: string) => http<{ ok: true }>("DELETE", `/api/admin/tenants/${t}/ffn`),
    ffnInvite: (t: string, email?: string) => http<{ url: string; expiresAt: string; sent: boolean }>("POST", `/api/admin/tenants/${t}/ffn/invite`, email ? { email } : {}),
    /** Logiful meldet sich selbst mit den FFN-Zugangsdaten des Kunden an */
    ffnConnectAsStaffUrl: (t: string) => `/ffn/connect?tenantId=${encodeURIComponent(t)}&from=admin`,
    inviteStaff: (email: string, role: StaffRole) => http<{ ok: true }>("POST", "/api/admin/staff/invites", { email, role }),
    alerts: () => http<SystemAlert[]>("GET", "/api/admin/alerts"),
    customerNotifications: () => http<{ enabled: boolean; since: string | null }>("GET", "/api/admin/settings/customer-notifications"),
    setCustomerNotifications: (enabled: boolean) => http<{ enabled: boolean; since: string | null }>("PUT", "/api/admin/settings/customer-notifications", { enabled }),
    systemStatus: () => http<SystemStatus>("GET", "/api/admin/system-status"),
    /** Kennzahlen-Leiste der Auftragsliste: zentral freigegebene Kennzahlen */
    orderKpis: () => http<OrderKpiSettings>("GET", "/api/admin/order-kpis"),
    saveOrderKpis: (s: OrderKpiSettings) => http<OrderKpiSettings>("PUT", "/api/admin/order-kpis", s),
    runChecks: () => http<{ ran: boolean; results: CheckResult[] }>("POST", "/api/admin/system-status/run"),
    failedJobs: () => http<FailedJob[]>("GET", "/api/admin/failed-jobs"),
    retryJob: (queue: string, id: string) => http<{ ok: true }>("POST", `/api/admin/failed-jobs/${queue}/${encodeURIComponent(id)}/retry`),
    removeJob: (queue: string, id: string) => http<void>("DELETE", `/api/admin/failed-jobs/${queue}/${encodeURIComponent(id)}`),
    setAlertWebhook: (url: string | null) => http<{ configured: boolean; host: string | null }>("PUT", "/api/admin/alert-webhook", { url }),
    alertTest: () => http<{ emails: number; webhook: "none" | "ok" | "failed"; webhookError: string | null }>("POST", "/api/admin/alert-test"),
    resolveAlert: (id: string) => http<{ ok: true }>("POST", `/api/admin/alerts/${id}/resolve`),
    returnSources: () => http<ReturnSource[]>("GET", "/api/admin/return-sources"),
    modules: () => http<AdminModuleOverview[]>("GET", "/api/admin/modules"),
    createModulePackage: (key: ModuleKey, body: ModulePackageInput) => http<{ id: string }>("POST", `/api/admin/modules/${key}/packages`, body),
    updateModulePackage: (id: string, body: ModulePackageInput) => http<{ ok: true }>("PUT", `/api/admin/module-packages/${id}`, body),
    moduleBundles: () => http<AdminModuleBundle[]>("GET", "/api/admin/module-bundles"),
    endCustomerMail: () => http<{ enabled: boolean; updatedAt: string | null }>("GET", "/api/admin/end-customer-mail"),
    announcements: () => http<AdminAnnouncement[]>("GET", "/api/admin/announcements"),
    createAnnouncement: (b: AnnouncementInputBody) => http<AdminAnnouncement>("POST", "/api/admin/announcements", b),
    updateAnnouncement: (id: string, b: AnnouncementInputBody) => http<AdminAnnouncement>("PUT", `/api/admin/announcements/${id}`, b),
    deleteAnnouncement: (id: string) => http<void>("DELETE", `/api/admin/announcements/${id}`),
    warehouseSites: () => http<WarehouseSiteItem[]>("GET", "/api/admin/warehouse-sites"),
    createWarehouseSite: (b: WarehouseSiteInput) => http<{ id: string }>("POST", "/api/admin/warehouse-sites", b),
    updateWarehouseSite: (id: string, b: WarehouseSiteInput) => http<{ ok: true }>("PUT", `/api/admin/warehouse-sites/${encodeURIComponent(id)}`, b),
    deleteWarehouseSite: (id: string) => http<void>("DELETE", `/api/admin/warehouse-sites/${encodeURIComponent(id)}`),
    inboundAppointments: (from: string, to: string) => http<AdminAppointment[]>("GET", `/api/admin/inbound-appointments${qs({ from, to })}`),
    deliveryAddresses: () => http<{ addresses: import("@logiful/domain").DeliveryAddresses; warehouses: { id: string; name: string }[] }>("GET", "/api/admin/delivery-addresses"),
    setDeliveryAddresses: (b: import("@logiful/domain").DeliveryAddresses) => http<import("@logiful/domain").DeliveryAddresses>("PUT", "/api/admin/delivery-addresses", b),
    setEndCustomerMail: (enabled: boolean) => http<{ enabled: boolean; updatedAt: string | null }>("PUT", "/api/admin/end-customer-mail", { enabled }),
    createBundle: (b: ModuleBundleInput) => http<{ id: string }>("POST", "/api/admin/module-bundles", b),
    updateBundle: (id: string, b: ModuleBundleInput) => http<{ ok: true }>("PUT", `/api/admin/module-bundles/${id}`, b),
    setTenantBundle: (t: string, id: string, a: AdminBundleAction & { tenantId?: string }) => http<{ ok: true }>("POST", `/api/admin/tenants/${t}/module-bundles/${id}`, a.action === "trial" ? { action: "trial", days: a.days } : { action: a.action }),
    setTenantModule: (t: string, key: ModuleKey, a: AdminModuleAction) => http<TenantModule>("POST", `/api/admin/tenants/${t}/modules/${key}`, a),
    unassignedClarifications: () => http<ReturnClarification[]>("GET", "/api/admin/return-clarifications"),
    createReturnSource: (body: { name: string; kind: ReturnSource["kind"]; connectionUrl: string }) => http<{ id: string }>("POST", "/api/admin/return-sources", body),
    updateReturnSource: (id: string, body: { name?: string; connectionUrl?: string }) => http<{ ok: true }>("PUT", `/api/admin/return-sources/${id}`, body),
    deleteReturnSource: (id: string) => http<{ ok: true }>("DELETE", `/api/admin/return-sources/${id}`),
    testReturnSource: (id: string) => http<{ ok: true } | { ok: false; error: string }>("POST", `/api/admin/return-sources/${id}/test`),
    returnSourceCustomers: (id: string) => http<{ customers: { key: string; name: string; number?: string | null; lines: number }[] }>("GET", `/api/admin/return-sources/${id}/customers`),
    syncReturnSource: (id: string) => http<{ queued: true }>("POST", `/api/admin/return-sources/${id}/sync`),
    setReturnsLink: (t: string, link: { sourceId: string; key: string } | null) => http<{ returnsLink: unknown }>("PUT", `/api/admin/tenants/${t}/returns-link`, link),
    setShippingMethodTracking: (id: string, body: { carrierName: string | null; urlTemplate: string | null }) => http<{ shippingMethodId: string; carrierName: string | null; urlTemplate: string | null }>("PUT", `/api/admin/shipping-method-tracking/${encodeURIComponent(id)}`, body),
    pushReturnToShopify: (t: string, caseId: string) => http<{ ok: true }>("POST", `/api/admin/tenants/${t}/returns/${caseId}/push-shopify`),
    testReturn: (t: string, orderId: string) => http<{ id: string }>("POST", `/api/admin/tenants/${t}/orders/${orderId}/test-return`),
    returnsPortal: () => http<{ configured: boolean; reachable: boolean; error: string | null; tenants: ReturnsPortalTenant[] }>("GET", "/api/admin/returns-portal"),
    setReturnsPortal: (t: string, body: { mode: "link"; portalTenantId: string } | { mode: "new" } | { mode: "unlink" }) => http<{ tenantId: string | null }>("PUT", "/api/admin/tenants/" + t + "/returns-portal", body),
    setReturnsShopify: (t: string, s: { mode: ReturnsShopifySettings["mode"]; autoRefund: boolean; reportUndeliverable?: boolean }) => http<ReturnsShopifySettings>("PUT", `/api/admin/tenants/${t}/returns-shopify`, s),
  },
  deliveryWidget: {
    get: (t: string) => http<DeliveryWidgetState>("GET", `/api/tenants/${t}/delivery-widget`),
    save: (t: string, s: DeliveryWidgetSettings) => http<{ settings: DeliveryWidgetSettings; key: string; snippets: { shopify: string; html: string } }>("PUT", `/api/tenants/${t}/delivery-widget`, s),
    preview: (t: string, b: { sku: string; country: string; settings: DeliveryWidgetSettings }) => http<DeliveryPromise & { available: number }>("POST", `/api/tenants/${t}/delivery-widget/preview`, b),
  },
  costs: (t: string) => http<CustomerCosts | { enabled: false }>("GET", `/api/tenants/${t}/costs`),
  validationSettings: {
    get: (t: string) => http<ValidationSettingsView>("GET", `/api/tenants/${t}/settings/validation`),
    save: (t: string, body: { shipsOutsideEu: boolean; rules: Record<string, "error" | "warning" | "off"> }) => http<ValidationSettingsView>("PUT", `/api/tenants/${t}/settings/validation`, body),
  },
  productRules: {
    get: (t: string) => http<ProductRulesState>("GET", `/api/tenants/${t}/product-rules`),
    preview: (t: string, definition: ProductRuleSet) => http<ProductRulePreview>("POST", `/api/tenants/${t}/product-rules/preview`, { definition }),
    save: (t: string, definition: ProductRuleSet) => http<{ version: number }>("POST", `/api/tenants/${t}/product-rules`, { definition }),
    activate: (t: string, version: number) => http<{ version: number }>("POST", `/api/tenants/${t}/product-rules/${version}/activate`),
    apply: (t: string) => http<{ queued: true }>("POST", `/api/tenants/${t}/product-rules/apply`),
    /** Standardartikelgewicht */
    defaultWeight: (t: string) => http<{ kg: number | null; products: number }>("GET", `/api/tenants/${t}/product-rules/default-weight`),
    setDefaultWeight: (t: string, kg: number | null) => http<{ kg: number | null }>("PUT", `/api/tenants/${t}/product-rules/default-weight`, { kg }),
  },
  production: {
    settings: (t: string) => http<ProductionSettings>("GET", `/api/tenants/${t}/production/settings`),
    saveSettings: (t: string, b: { address?: Address; shippingMethodId?: string | null }) => http<ProductionSettings>("PUT", `/api/tenants/${t}/production/settings`, b),
    boms: (t: string) => http<{ items: ProductionBomItem[] }>("GET", `/api/tenants/${t}/production/boms`),
    bom: (t: string, productId: string) => http<ProductionBomDetail>("GET", `/api/tenants/${t}/production/boms/${productId}`),
    saveBom: (t: string, productId: string, bom: ProductionBomInput) => http<{ version: number; bom: ProductionBomDef; warning: string | null }>("PUT", `/api/tenants/${t}/production/boms/${productId}`, bom),
    archiveBom: (t: string, productId: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/production/boms/${productId}`),
    uploadBomFile: (t: string, productId: string, f: { filename: string; data: string }) => http<{ id: string }>("POST", `/api/tenants/${t}/production/boms/${productId}/files`, f),
    fileUrl: (t: string, fileId: string) => `/api/tenants/${t}/production/files/${fileId}`,
    deleteFile: (t: string, fileId: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/production/files/${fileId}`),
    orders: (t: string, status: "open" | "done" | "all" = "open") => http<{ items: ProductionOrderRow[] }>("GET", `/api/tenants/${t}/production/orders?status=${status}`),
    order: (t: string, id: string) => http<ProductionOrderDetail>("GET", `/api/tenants/${t}/production/orders/${id}`),
    preview: (t: string, b: { productId: string; quantity: number; kind?: ProductionKind }) => http<ProductionPreview>("POST", `/api/tenants/${t}/production/orders/preview`, b),
    create: (t: string, b: { productId: string; quantity: number; kind?: ProductionKind; dueDate?: string | null; note?: string }) => http<{ id: string; number: string }>("POST", `/api/tenants/${t}/production/orders`, b),
    cancel: (t: string, id: string, reason?: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/cancel`, reason ? { reason } : {}),
    reject: (t: string, id: string, reason: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/reject`, { reason }),
    accept: (t: string, id: string, b: { plannedDate?: string | null; quote?: { lines: QuoteLine[]; note?: string }; internalNote?: string }) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/accept`, b),
    saveQuote: (t: string, id: string, q: { lines: QuoteLine[]; note?: string }) => http<{ ok: true }>("PUT", `/api/tenants/${t}/production/orders/${id}/quote`, q),
    update: (t: string, id: string, b: { plannedDate?: string | null; internalNote?: string | null }) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/production/orders/${id}`, b),
    start: (t: string, id: string, shippingMethodId?: string | null) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/start`, shippingMethodId ? { shippingMethodId } : {}),
    uploadSample: (t: string, id: string, f: { filename: string; data: string }) => http<{ id: string }>("POST", `/api/tenants/${t}/production/orders/${id}/sample/files`, f),
    submitSample: (t: string, id: string, note?: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/sample/submit`, note ? { note } : {}),
    decideSample: (t: string, id: string, b: { approved: boolean; feedback?: string }) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/sample/decision`, b),
    complete: (t: string, id: string, b: ProductionCompleteInput) => http<{ ok: true; inboundId: string | null }>("POST", `/api/tenants/${t}/production/orders/${id}/complete`, b),
    billed: (t: string, id: string, billed: boolean) => http<{ ok: true }>("POST", `/api/tenants/${t}/production/orders/${id}/billed`, { billed }),
  },
  profit: {
    report: (t: string, q: { from?: string; to?: string; channelId?: string }) => http<ProfitReport>("GET", `/api/tenants/${t}/profit/report?${new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][])}`),
    settings: (t: string) => http<{ settings: ProfitSettings; channels: { id: string; name: string; kind: string }[] }>("GET", `/api/tenants/${t}/profit/settings`),
    saveSettings: (t: string, s: ProfitSettings) => http<{ settings: ProfitSettings }>("PUT", `/api/tenants/${t}/profit/settings`, s),
    costs: (t: string, q: { search?: string; missing?: boolean }) => http<{ items: ProductCostItem[] }>("GET", `/api/tenants/${t}/profit/costs?${new URLSearchParams({ ...(q.search ? { search: q.search } : {}), ...(q.missing ? { missing: "1" } : {}) })}`),
    setCost: (t: string, productId: string, cost: number) => http<{ ok: true }>("PUT", `/api/tenants/${t}/profit/costs/${productId}`, { cost }),
    costHistory: (t: string, productId: string) => http<{ items: ProductCostEvent[] }>("GET", `/api/tenants/${t}/profit/costs/${productId}/history`),
    inbounds: (t: string) => http<{ items: ProfitInbound[] }>("GET", `/api/tenants/${t}/profit/inbounds`),
    setInboundLineCost: (t: string, inboundId: string, lineId: string, unitCost: number | null) => http<{ ok: true }>("PUT", `/api/tenants/${t}/profit/inbounds/${inboundId}/lines/${lineId}`, { unitCost }),
    status: (t: string) => http<{ shops: ProfitShopStatus[] }>("GET", `/api/tenants/${t}/profit/status`),
    sync: (t: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/profit/sync`),
    manualCosts: (t: string) => http<{ monthly: ProfitManualCost[]; orders: ProfitManualCost[] }>("GET", `/api/tenants/${t}/profit/manual-costs`),
    addManualCost: (t: string, b: ProfitManualCostInput) => http<ProfitManualCost>("POST", `/api/tenants/${t}/profit/manual-costs`, b),
    updateManualCost: (t: string, id: string, b: ProfitManualCostInput) => http<ProfitManualCost>("PUT", `/api/tenants/${t}/profit/manual-costs/${id}`, b),
    deleteManualCost: (t: string, id: string) => http<void>("DELETE", `/api/tenants/${t}/profit/manual-costs/${id}`),
  },
  integrations: {
    keys: (t: string) => http<ApiKeyItem[]>("GET", `/api/tenants/${t}/api-keys`),
    createKey: (t: string, name: string, permissions: StoredPermissions) => http<{ id: string; key: string; prefix: string }>("POST", `/api/tenants/${t}/api-keys`, { name, permissions }),
    revokeKey: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/api-keys/${id}`),
    webhooks: (t: string) => http<WebhookItem[]>("GET", `/api/tenants/${t}/webhooks`),
    createWebhook: (t: string, body: { url: string; description?: string; events: string[] }) => http<{ id: string; secret: string }>("POST", `/api/tenants/${t}/webhooks`, body),
    updateWebhook: (t: string, id: string, body: { url?: string; description?: string | null; events?: string[]; active?: boolean }) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/webhooks/${id}`, body),
    deleteWebhook: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/webhooks/${id}`),
    rotateSecret: (t: string, id: string) => http<{ secret: string }>("POST", `/api/tenants/${t}/webhooks/${id}/rotate-secret`),
    testWebhook: (t: string, id: string) => http<{ ok: boolean; status: number | null; error: string | null; body: string | null }>("POST", `/api/tenants/${t}/webhooks/${id}/test`),
    deliveries: (t: string, id: string, status?: "pending" | "success" | "failed") => http<WebhookDelivery[]>("GET", `/api/tenants/${t}/webhooks/${id}/deliveries${qs({ status })}`),
    redeliver: (t: string, ids: string[]) => http<{ queued: number }>("POST", `/api/tenants/${t}/webhooks/redeliver`, { ids }),
    openapi: () => http<{ paths: Record<string, Record<string, { summary?: string; description?: string; tags?: string[] }>>; webhooks: Record<string, { post: { summary: string } }> }>("GET", "/v1/openapi.json"),
  },
  trackingOverview: (t: string, days: number) => http<TrackingOverview>("GET", `/api/tenants/${t}/tracking/overview${qs({ days })}`),
  apps: {
    get: (t: string) => http<AppsView>("GET", `/api/tenants/${t}/apps`),
    connectKlaviyo: (t: string, body: { apiKey: string; events: string[] }) => http<{ ok: true; account: string | null; message: string | null }>("POST", `/api/tenants/${t}/apps/klaviyo`, body),
    updateKlaviyo: (t: string, body: { events?: string[]; active?: boolean; apiKey?: string }) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/apps/klaviyo`, body),
    disconnectKlaviyo: (t: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/apps/klaviyo`),
    klaviyoDeliveries: (t: string) => http<KlaviyoDelivery[]>("GET", `/api/tenants/${t}/apps/klaviyo/deliveries`),
    klaviyoTest: (t: string, email: string) => http<{ ok: true; message: string }>("POST", `/api/tenants/${t}/apps/klaviyo/test`, { email }),
  },
  carrierAccounts: {
    list: (t: string) => http<CarrierAccountsView>("GET", `/api/tenants/${t}/carrier-accounts`),
    create: (t: string, body: CarrierAccountInput) => http<{ account: CarrierAccount; check: CarrierCheck | null }>("POST", `/api/tenants/${t}/carrier-accounts`, body),
    update: (t: string, id: string, body: CarrierAccountPatch) => http<{ account: CarrierAccount; check: CarrierCheck | null }>("PATCH", `/api/tenants/${t}/carrier-accounts/${id}`, body),
    check: (t: string, id: string) => http<CarrierCheck>("POST", `/api/tenants/${t}/carrier-accounts/${id}/check`),
    remove: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/carrier-accounts/${id}`),
    admin: () => http<AdminCarrierAccountsView>("GET", "/api/admin/carrier-accounts"),
    adminCreate: (body: CarrierAccountInput) => http<{ account: CarrierAccount; check: CarrierCheck | null }>("POST", "/api/admin/carrier-accounts", body),
    adminUpdate: (id: string, body: CarrierAccountPatch) => http<{ account: CarrierAccount; check: CarrierCheck | null }>("PATCH", `/api/admin/carrier-accounts/${id}`, body),
    adminCheck: (id: string) => http<CarrierCheck>("POST", `/api/admin/carrier-accounts/${id}/check`),
    adminTestKey: (apiKey?: string) => http<{ ok: boolean; message: string; hint: string | null; source: "input" | "configured" }>("POST", "/api/admin/carrier-accounts/test-key", apiKey ? { apiKey } : {}),
    adminRemove: (id: string) => http<{ ok: true }>("DELETE", `/api/admin/carrier-accounts/${id}`),
  },
  security: {
    mfa: () => http<MfaStatus>("GET", "/api/me/mfa"),
    setup: () => http<{ secret: string; otpauthUrl: string }>("POST", "/api/me/mfa/setup"),
    enable: (code: string) => http<{ recoveryCodes: string[] }>("POST", "/api/me/mfa/enable", { code }),
    disable: (code: string) => http<{ ok: true }>("POST", "/api/me/mfa/disable", { code }),
    renewCodes: (code: string) => http<{ recoveryCodes: string[] }>("POST", "/api/me/mfa/recovery-codes", { code }),
    team: (t: string) => http<TeamSecurity>("GET", `/api/tenants/${t}/security`),
    setRequireMfa: (t: string, requireMfa: boolean) => http<{ requireMfa: boolean }>("PUT", `/api/tenants/${t}/security`, { requireMfa }),
    resetMember: (t: string, userId: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/members/${userId}/mfa-reset`),
    audit: (t: string, q: AuditQuery) => http<{ items: AuditEntry[]; next: string | null }>("GET", `/api/tenants/${t}/audit${qs(q)}`),
    staffMfa: () => http<{ required: boolean }>("GET", "/api/admin/settings/staff-mfa"),
    setStaffMfa: (required: boolean) => http<{ required: boolean }>("PUT", "/api/admin/settings/staff-mfa", { required }),
  },
  performance: (t: string, days: 7 | 30 | 90) => http<Performance>("GET", `/api/tenants/${t}/performance?days=${days}`),
  notifications: {
    get: (t: string) => http<NotificationState>("GET", `/api/tenants/${t}/notifications/settings`),
    save: (t: string, s: NotificationSettings) => http<NotificationSettings>("PUT", `/api/tenants/${t}/notifications/settings`, s),
    test: (t: string, kind: "daily" | "weekly") => http<{ sent: number }>("POST", `/api/tenants/${t}/notifications/test`, { kind }),
  },
  actionItems: {
    get: (t: string) => http<ActionItems>("GET", `/api/tenants/${t}/action-items`),
    ack: (t: string, keys: string[], undo = false) => http<{ changed: number }>("POST", `/api/tenants/${t}/action-items/ack`, { keys, undo }),
  },
  modules: {
    list: (t: string) => http<TenantModule[]>("GET", `/api/tenants/${t}/modules`),
    book: (t: string, key: ModuleKey, packageId: string) => http<{ change: "booked" | "reactivated" | "upgraded" | "downgrade_scheduled" | "switched"; phase: ModulePhase; trialEndsAt: string | null; pendingFrom: string | null }>("POST", `/api/tenants/${t}/modules/${key}/book`, { acceptTerms: true, packageId }),
    cancel: (t: string, key: ModuleKey) => http<{ cancelAt: string }>("POST", `/api/tenants/${t}/modules/${key}/cancel`, {}),
    bundles: (t: string) => http<ModuleBundle[]>("GET", `/api/tenants/${t}/module-bundles`),
    bookBundle: (t: string, id: string) => http<{ change: "booked" | "reactivated"; phase: ModulePhase; trialEndsAt: string | null }>("POST", `/api/tenants/${t}/module-bundles/${id}/book`, { acceptTerms: true }),
    cancelBundle: (t: string, id: string) => http<{ cancelAt: string }>("POST", `/api/tenants/${t}/module-bundles/${id}/cancel`, {}),
  },
  returns: {
    list: (t: string, q: { q?: string; status?: ReturnStatus; undeliverable?: "1"; condition?: "A" | "B" | "C"; page?: number }) => http<ReturnsList>("GET", `/api/tenants/${t}/returns${qs(q)}`),
    get: (t: string, id: string) => http<ReturnCaseDetail>("GET", `/api/tenants/${t}/returns/${id}`),
    ofOrder: (t: string, orderId: string) => http<OrderReturn[]>("GET", `/api/tenants/${t}/orders/${orderId}/returns`),
    clarifications: (t: string) => http<ReturnClarification[]>("GET", `/api/tenants/${t}/return-clarifications`),
    analytics: (t: string, q: { days: 30 | 90 | 180 | 365; search?: string }) => http<ReturnsAnalytics>("GET", `/api/tenants/${t}/returns/analytics${qs(q)}`),
    /** Retourenportal: eingerichtet? / Login-Sprung */
    portal: (t: string) => http<ReturnsPortalInfo>("GET", `/api/tenants/${t}/returns-portal`),
    openPortal: (t: string, o: { to?: "returns" | "flow" | "settings" | "branding"; q?: string } = {}) => http<{ url: string }>("POST", `/api/tenants/${t}/returns-portal/open`, o),
  },
  address: {
    dhl: () => http<DhlStatus>("GET", "/api/admin/dhl"),
    /** DataFactory-ZIP als Rohdaten hochladen (bis 120 MB) */
    uploadDhl: async (file: File): Promise<{ version: string }> => {
      const res = await fetch(`/api/admin/dhl/upload?name=${encodeURIComponent(file.name.replace(/\.zip$/i, ""))}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/zip" }, body: file });
      const json = await res.json().catch(() => undefined);
      if (!res.ok) throw new ApiError(res.status, json);
      return json as { version: string };
    },
    queue: (mode: "logiful" | "all" = "logiful") => http<AddressCase[]>("GET", `/api/admin/address-queue?mode=${mode}`),
    check: (address: ShippingAddress) => http<AddressCheckResult>("POST", "/api/admin/address/check", address),
    fix: (orderId: string, address: ShippingAddress, confirm = false) => http<{ ok: true }>("POST", `/api/admin/orders/${orderId}/address`, { address, confirm }),
    tenants: () => http<AddressTenant[]>("GET", "/api/admin/address-check/tenants"),
    houseNumberInAddition: () => http<HouseNumberInAddition[]>("GET", "/api/admin/address/house-number-in-addition"),
    setTenant: (tenantId: string, settings: AddressCheckSettings) => http<AddressCheckSettings>("PUT", `/api/admin/tenants/${tenantId}/address-check`, settings),
  },
  products: {
    list: (t: string, q: ProductQuery) => http<Page<ProductItem>>("GET", `/api/tenants/${t}/products${qs(q)}`),
    summary: (t: string) => http<ProductSummary>("GET", `/api/tenants/${t}/products/summary`),
    get: (t: string, id: string) => http<ProductItem>("GET", `/api/tenants/${t}/products/${id}`),
    setReplenishment: (t: string, id: string, r: ProductReplenishment) => http<{ replenishment: ProductReplenishment }>("PUT", `/api/tenants/${t}/products/${id}/replenishment`, r),
    sales: (t: string, id: string, days = 90) => http<{ days: number; series: { day: string; units: number; orders: number }[] }>("GET", `/api/tenants/${t}/products/${id}/sales?days=${days}`),
    confirmWeight: (t: string, id: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/products/${id}/weight-review/confirm`, {}),
    /** von Hand gepflegtes Gewicht verwerfen → wieder Standardgewicht */
    useDefaultWeight: (t: string, id: string) => http<{ ok: true; kg: number }>("POST", `/api/tenants/${t}/products/${id}/default-weight`, {}),
    confirmPlausibility: (t: string, id: string, ruleId: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/products/${id}/plausibility/${ruleId}/confirm`, {}),
    setLock: (t: string, id: string, field: string, locked: boolean) => http<{ lockedFields: string[] }>("PUT", `/api/tenants/${t}/products/${id}/locks`, { field, locked }),
    patch: (t: string, id: string, patch: ProductPatch) => http<{ changed: boolean }>("PATCH", `/api/tenants/${t}/products/${id}`, patch),
    create: (t: string, body: ProductPatch & { sku: string; name: string; transfer: boolean }) => http<{ id: string; transferQueued: boolean; validation: { status: string; syncBlocked: boolean } | null }>("POST", `/api/tenants/${t}/products`, body),
    transfer: (t: string, id: string) => http<{ queued: true }>("POST", `/api/tenants/${t}/products/${id}/transfer`),
    bulkPackUnit: (t: string, ids: string[], mode: "add" | "replace" | "remove", unit: { name: string; quantity: number }) => http<{ requested: number; changed: number }>("POST", `/api/tenants/${t}/products/bulk-pack-unit`, { ids, mode, unit }),
    bulk: (t: string, ids: string[], patch: ProductPatch) => http<{ requested: number; changed: number; blocked?: { id: string; sku: string; reasons: string[] }[] }>("POST", `/api/tenants/${t}/products/bulk`, { ids, patch }),
    /** für Logiful freigeben – einzeln oder in Massen */
    release: (t: string, ids: string[]) => http<ReleaseResult>("POST", `/api/tenants/${t}/products/release`, { ids }),
    /** Bestand an einen Shop (channelId) oder alle verknüpften Shops übertragen */
    pushStock: (t: string, id: string, channelId?: string) => http<{ channels: number; reactivated?: boolean }>("POST", `/api/tenants/${t}/products/${id}/push-stock`, channelId ? { channelId } : {}),
    import: (t: string) => http<{ queued: number; needsFilter: { id: string; name: string }[] }>("POST", `/api/tenants/${t}/products/import`),
    setBundle: (t: string, id: string, components: { productId: string; quantity: number }[] | null) =>
      http<{ changed: boolean }>("PUT", `/api/tenants/${t}/products/${id}/bundle`, { components }),
  },
  orders: {
    list: (t: string) => http<OrderItem[]>("GET", `/api/tenants/${t}/orders`),
    summary: (t: string) => http<OrderSummary>("GET", `/api/tenants/${t}/orders/summary`),
    /** Kennzahlen-Leiste (nur von Logiful freigegebene Kennzahlen) */
    kpis: (t: string, range: OrderKpiRange) => http<{ range: OrderKpiRange; items: OrderKpiValue[] }>("GET", `/api/tenants/${t}/orders/kpis?range=${range}`),
    update: (t: string, id: string, body: Partial<Pick<NewManualOrder, "address" | "lines" | "reference" | "express">> & { shippingMethodId?: string | null; note?: string | null }) => http<{ ok: true; changed: string[] }>("PATCH", `/api/tenants/${t}/orders/${id}`, body),
    /** stornierten Auftrag erneut bei Logiful anfragen (derselbe Auftrag, im Lager „…-2“, folgt allen Regeln) */
    rerequest: (t: string, id: string) => http<{ orderId: string; warehouseOrderNumber: string }>("POST", `/api/tenants/${t}/orders/${id}/rerequest`),
    /** Bei Logiful stornieren; Shop-Aufträge: logiful = Shop unverändert, return_to_shop = in Shopify an den Händler zurückgeben */
    cancel: (t: string, id: string, mode?: "logiful" | "return_to_shop") => http<{ ok: true; inWarehouse?: boolean }>("POST", `/api/tenants/${t}/orders/${id}/cancel`, mode ? { mode } : {}),
    /** Freigabetermin setzen (YYYY-MM-DD) oder aufheben (null) */
    /** aus dem Lager zurückholen (release: wieder freigeben) */
    recall: (t: string, id: string, release?: boolean) => http<{ ok: true }>("POST", `/api/tenants/${t}/orders/${id}/recall`, release ? { release } : {}),
    defer: (t: string, id: string, until: string | null) => http<{ ok: true; until?: string; inWarehouse?: boolean }>("POST", `/api/tenants/${t}/orders/${id}/defer`, { until }),
    /** Übertragung trotz Sperre erzwingen */
    force: (t: string, id: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/orders/${id}/force`),
    create: (t: string, body: NewManualOrder) => http<{ id: string; orderNumber: string; addressBookId: string | null }>("POST", `/api/tenants/${t}/orders`, body),
    search: (t: string, q: OrderSearchQuery) => http<OrderSearchResult>("GET", `/api/tenants/${t}/orders/search${qs(q)}`),
    /** Sammelaktionen: IDs aller Treffer des Filters (max. 500) */
    matchIds: (t: string, q: OrderSearchQuery) => http<{ ids: string[]; statuses: Record<string, OrderStatus>; total: number; capped: boolean }>("GET", `/api/tenants/${t}/orders/search${qs({ ...q, idsOnly: "1" })}`),
    get: (t: string, id: string) => http<OrderDetail>("GET", `/api/tenants/${t}/orders/${id}`),
    reprocess: (t: string, id: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/orders/${id}/reprocess`),
    refetch: (t: string, id: string) => http<{ queued?: boolean; changes: string[]; addressKept: boolean }>("POST", `/api/tenants/${t}/orders/${id}/refetch`),
    retryChannelReport: (t: string, id: string) => http<{ queued: boolean; shipments: number }>("POST", `/api/tenants/${t}/orders/${id}/retry-channel-report`),
    reservation: (t: string, id: string) => http<OrderReservation>("GET", `/api/tenants/${t}/orders/${id}/reservation`),
    boostReservation: (t: string, id: string, boost: boolean) => http<{ ok: true }>("POST", `/api/tenants/${t}/orders/${id}/reservation-boost`, { boost }),
    reservationSettings: (t: string) => http<{ settings: ReservationSettings; channels: { id: string; name: string; kind: string }[] }>("GET", `/api/tenants/${t}/settings/reservation`),
    saveReservationSettings: (t: string, s: ReservationSettings) => http<ReservationSettings>("PUT", `/api/tenants/${t}/settings/reservation`, s),
    reservationPreview: (t: string) => http<{ strategy: "fifo" | "max_orders"; open: number; fifo: number; maxOrders: number }>("GET", `/api/tenants/${t}/settings/reservation/preview`),
    addressRequest: (t: string, id: string) => http<AddressRequestState>("GET", `/api/tenants/${t}/orders/${id}/address-request`),
    requestAddress: (t: string, id: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/orders/${id}/address-request`),
    /** trotz Fehlbestand übertragen (bis zu Logiful) bzw. im FFN geparkten Auftrag an Logiful übergeben */
    partialPreview: (t: string, id: string) => http<{ ship: { sku: string; name: string; quantity: number }[]; rest: { sku: string; name: string; quantity: number }[] }>("GET", `/api/tenants/${t}/orders/${id}/partial-delivery`),
    partialDeliver: (t: string, id: string) => http<{ result: string; restOrderId?: string; restOrderNumber?: string }>("POST", `/api/tenants/${t}/orders/${id}/partial-delivery`),
    backorderRelease: (t: string, id: string) => http<{ queued: boolean; handOver: boolean }>("POST", `/api/tenants/${t}/orders/${id}/backorder-release`),
    backorderSettings: (t: string) => http<BackorderSettings>("GET", `/api/tenants/${t}/settings/backorder`),
    exportDocument: (t: string, id: string) => http<ExportDocumentState>("GET", `/api/tenants/${t}/orders/${id}/export-document`),
    uploadExportDocument: (t: string, id: string, body: { filename: string; data: string }) => http<{ id: string }>("POST", `/api/tenants/${t}/orders/${id}/export-document`, body),
    deleteExportDocument: (t: string, id: string, docId: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/orders/${id}/export-document/${docId}`),
    approveExportDocument: (t: string, id: string) => http<{ ok: true }>("POST", `/api/tenants/${t}/orders/${id}/export-document/approve`, {}),
    exportDocumentUrl: (t: string, id: string, docId: string) => `/api/tenants/${t}/orders/${id}/export-document/${docId}`,
    exportDocumentSettings: (t: string) => http<ExportDocumentSettings>("GET", `/api/tenants/${t}/settings/export-document`),
    saveExportDocumentSettings: (t: string, s: ExportDocumentSettings) => http<ExportDocumentSettings>("PUT", `/api/tenants/${t}/settings/export-document`, s),
    saveBackorderSettings: (t: string, s: BackorderSettings) => http<BackorderSettings>("PUT", `/api/tenants/${t}/settings/backorder`, s),
  },
  addressBook: {
    list: (t: string, q?: string) => http<AddressBookEntry[]>("GET", `/api/tenants/${t}/address-book${qs({ q })}`),
    create: (t: string, body: { label?: string; address: Address }) => http<{ id: string }>("POST", `/api/tenants/${t}/address-book`, body),
    update: (t: string, id: string, body: { label?: string | null; address?: Address }) => http<{ ok: true }>("PATCH", `/api/tenants/${t}/address-book/${id}`, body),
    remove: (t: string, id: string) => http<{ ok: true }>("DELETE", `/api/tenants/${t}/address-book/${id}`),
  },
  shipping: {
    packagings: () => http<PackagingItem[]>("GET", "/api/admin/packagings"),
    createPackaging: (b: PackagingInput) => http<PackagingItem>("POST", "/api/admin/packagings", b),
    updatePackaging: (id: string, b: Partial<PackagingInput>) => http<PackagingItem>("PATCH", `/api/admin/packagings/${id}`, b),
    deletePackaging: (id: string) => http<{ ok: true }>("DELETE", `/api/admin/packagings/${id}`),
    /** ohne tenantId: allgemeine Profile; mit tenantId: zusätzlich die Profile dieses Kunden */
    profiles: (tenantId?: string) => http<ShippingProfilesState>("GET", `/api/admin/shipping-profiles${qs({ tenantId })}`),
    saveProfile: (methodId: string, def: Omit<ShippingProfile, "methodId">, tenantId?: string) => http<{ ok: true }>("PUT", `/api/admin/shipping-profiles/${encodeURIComponent(methodId)}${qs({ tenantId })}`, def),
    deleteProfile: (methodId: string, tenantId?: string) => http<{ ok: true }>("DELETE", `/api/admin/shipping-profiles/${encodeURIComponent(methodId)}${qs({ tenantId })}`),
    globalRules: () => http<{ version: number; rules: ShippingRule[] }>("GET", "/api/admin/shipping-rules"),
    saveGlobalRules: (rules: ShippingRule[]) => http<{ version: number }>("PUT", "/api/admin/shipping-rules", { rules }),
    rules: (t: string) => http<ShippingRulesState>("GET", `/api/tenants/${t}/shipping-rules`),
    saveRules: (t: string, rules: ShippingRule[], settings?: ShippingSettings) => http<{ version: number }>("PUT", `/api/tenants/${t}/shipping-rules`, { rules, ...(settings ? { settings } : {}) }),
    simulate: (t: string, body: { rules?: ShippingRule[]; settings?: ShippingSettings; days?: number }) => http<SimulationResult>("POST", `/api/tenants/${t}/shipping-rules/simulate`, body),
  },
  channels: {
    list: (t: string) => http<ChannelItem[]>("GET", `/api/tenants/${t}/channels`),
    get: (t: string, id: string) => http<ChannelDetail>("GET", `/api/tenants/${t}/channels/${id}`),
    sftp: (t: string, id: string) => http<SftpChannel>("GET", `/api/tenants/${t}/channels/${id}/sftp`),
    createSftp: (t: string, body: { name: string; config: SftpConfig; secret: string }) => http<{ id: string; waitingFiles: number }>("POST", `/api/tenants/${t}/channels/sftp`, body),
    updateSftp: (t: string, id: string, body: { name?: string; config: SftpConfig; secret?: string }) => http<{ ok: boolean; waitingFiles: number }>("PUT", `/api/tenants/${t}/channels/${id}/sftp`, body),
    testSftp: (t: string, body: { config: SftpConfig; secret?: string; channelId?: string }) => http<{ ok: boolean; waitingFiles: number; problems: string[] }>("POST", `/api/tenants/${t}/channels/sftp/test`, body),
    runSftp: (t: string, id: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/channels/${id}/sftp/run`),
    setStockSync: (t: string, id: string, v: import("@logiful/domain").ChannelStockSync) => http<{ stockSync: import("@logiful/domain").ChannelStockSync }>("PUT", `/api/tenants/${t}/channels/${id}/stock-sync`, v),
    pauseSftp: (t: string, id: string, active: boolean) => http<{ active: boolean }>("POST", `/api/tenants/${t}/channels/${id}/sftp/pause`, { active }),
    marketplaceKinds: (t: string) => http<{ kinds: { kind: MarketplaceKind; label: string; available: boolean; licensed: boolean }[] }>("GET", `/api/tenants/${t}/channels/marketplaces/kinds`),
    marketplace: (t: string, id: string) => http<MarketplaceChannel>("GET", `/api/tenants/${t}/channels/${id}/marketplace`),
    testMarketplace: (t: string, body: { kind: MarketplaceKind; config: Record<string, unknown>; secret?: string; channelId?: string }) => http<{ ok: boolean; name?: string; message?: string }>("POST", `/api/tenants/${t}/channels/marketplace/test`, body),
    createMarketplace: (t: string, body: { kind: MarketplaceKind; name: string; config: Record<string, unknown>; secret: string; options: { pushStock: boolean; importDays: number } }) => http<{ id: string; shopName: string }>("POST", `/api/tenants/${t}/channels/marketplace`, body),
    odooDescribe: (t: string, body: { config: Record<string, unknown>; secret?: string; channelId?: string }) => http<OdooDescribe>("POST", `/api/tenants/${t}/channels/marketplace/odoo/describe`, body),
    updateMarketplace: (t: string, id: string, body: { name?: string; config: Record<string, unknown>; secret?: string; pushStock?: boolean; autoCreateProducts?: boolean }) => http<{ ok: boolean; shopName: string }>("PUT", `/api/tenants/${t}/channels/${id}/marketplace`, body),
    runMarketplace: (t: string, id: string, fullStock = false) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/channels/${id}/marketplace/run`, { fullStock }),
    findChannelOrders: (t: string, id: string, q: ChannelOrderQuery) => http<ChannelOrderSearch>("POST", `/api/tenants/${t}/channels/${id}/marketplace/find-orders`, q),
    findShopifyOrders: (t: string, id: string, q: ChannelOrderQuery) => http<ChannelOrderSearch>("POST", `/api/tenants/${t}/channels/${id}/shopify/find-orders`, q),
    importShopifyOrders: (t: string, id: string, query: ChannelOrderQuery, externalIds: string[]) => http<ChannelOrderImport>("POST", `/api/tenants/${t}/channels/${id}/shopify/import-orders`, { query, externalIds }),
    importChannelOrders: (t: string, id: string, query: ChannelOrderQuery, externalIds: string[]) => http<ChannelOrderImport>("POST", `/api/tenants/${t}/channels/${id}/marketplace/import-orders`, { query, externalIds }),
    shopHealth: (t: string, id: string) => http<{ checkedAt: string; checks: ShopHealthCheck[] }>("GET", `/api/tenants/${t}/channels/${id}/shopify/health`),
    shopHealthFix: (t: string, id: string, fix: NonNullable<ShopHealthCheck["fix"]>) => http<{ ok: true; message: string }>("POST", `/api/tenants/${t}/channels/${id}/shopify/health/fix`, { fix }),
    marketplaceShippingMethods: (t: string, id: string) => http<{ supported: boolean; methods: string[]; error?: string }>("GET", `/api/tenants/${t}/channels/${id}/marketplace/shipping-methods`),
    pauseMarketplace: (t: string, id: string, active: boolean) => http<{ active: boolean }>("POST", `/api/tenants/${t}/channels/${id}/marketplace/pause`, { active }),
    setDeliveryNotifications: (t: string, id: string, source: "none" | "shopify" | "klaviyo") => http<{ source: string }>("PUT", `/api/tenants/${t}/channels/${id}/delivery-notifications`, { source }),
    setShippingMapping: (t: string, id: string, m: ShopMethodMapping) => http<ShopMethodMapping>("PUT", `/api/tenants/${t}/channels/${id}/shipping-mapping`, m),
    setNightlySync: (t: string, id: string, enabled: boolean) => http<{ enabled: boolean }>("PUT", `/api/tenants/${t}/channels/${id}/nightly-sync`, { enabled }),
    setFillMissingSku: (t: string, id: string, enabled: boolean) => http<{ enabled: boolean }>("PUT", `/api/tenants/${t}/channels/${id}/fill-missing-sku`, { enabled }),
    pushStock: (t: string, id: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/channels/${id}/push-stock`),
    fetchOrders: (t: string, id: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/channels/${id}/fetch-orders`),
    setAutoRequest: (t: string, id: string, mode: "all" | "paid" | "off") => http<{ mode: string }>("PUT", `/api/tenants/${t}/channels/${id}/auto-request`, { mode }),
    /** Kanal entfernen: ohne Aufträge löschen, sonst trennen (Historie bleibt) */
    remove: (t: string, id: string) => http<{ result: "deleted" | "disconnected"; orders: number }>("DELETE", `/api/tenants/${t}/channels/${id}`),
    setBackorder: (t: string, id: string, override: Partial<BackorderSettings>) => http<{ backorder: Partial<BackorderSettings> }>("PUT", `/api/tenants/${t}/channels/${id}/backorder`, override),
    setTrackingLink: (t: string, id: string, mode: "warehouse" | "dhl_de") => http<{ mode: string }>("PUT", `/api/tenants/${t}/channels/${id}/tracking-link`, { mode }),
    setPushStock: (t: string, id: string, enabled: boolean) => http<{ pushStock: boolean }>("PUT", `/api/tenants/${t}/channels/${id}/push-stock`, { enabled }),
    setLocationScope: (t: string, id: string, scope: "all" | "released") => http<{ scope: string }>("PUT", `/api/tenants/${t}/channels/${id}/location-scope`, { scope }),
    facets: (t: string, id: string) => http<{ vendors: string[]; productTypes: string[]; tags: string[] }>("GET", `/api/tenants/${t}/channels/${id}/catalog/facets`),
    preview: (t: string, id: string, filter: ImportFilter) => http<{ matching: number; matchingExact: boolean; total: number; postFilter: boolean }>("POST", `/api/tenants/${t}/channels/${id}/catalog/preview`, filter),
    startImport: (t: string, id: string, filter: ImportFilter) => http<{ runId: string }>("POST", `/api/tenants/${t}/channels/${id}/catalog/import`, { filter }),
    /** Shop vormerken (optional mit Custom-App-Zugangsdaten), liefert den Link zur Shopify-Installation */
    prepareShopify: (t: string, body: { shop: string; clientId?: string; clientSecret?: string }) => http<{ installUrl: string; shop: string; credentials: "shop" | "global" }>("POST", `/api/tenants/${t}/channels/shopify`, body),
  },
  stock: {
    syncNow: (t: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/stock/sync`),
    syncSettings: (t: string) => http<{ includeAnnounced: boolean; bufferUnits: number }>("GET", `/api/tenants/${t}/settings/stock-sync`),
    saveSyncSettings: (t: string, s: { includeAnnounced: boolean; bufferUnits: number }) => http<{ includeAnnounced: boolean; bufferUnits: number }>("PUT", `/api/tenants/${t}/settings/stock-sync`, s),
    list: (t: string, search?: string) => http<StockItem[]>("GET", `/api/tenants/${t}/stock${qs({ search })}`),
    /** große Sortimente: Suche, Filter, Sortierung, Seiten und Kennzahlen auf dem Server */
    page: (t: string, q: StockPageQuery) => http<StockPage>("GET", `/api/tenants/${t}/stock/page${qs(q)}`),
    bestBefore: (t: string) => http<BestBeforeState>("GET", `/api/tenants/${t}/stock/best-before`),
    bestBeforePage: (t: string, q: { page: number; pageSize?: 50 | 100 | 250; level: BestBeforeLevel | "attention"; search?: string }) => http<BestBeforeState & { total: number; page: number; pageSize: number }>("GET", `/api/tenants/${t}/stock/best-before${qs(q)}`),
    bestBeforePreview: (t: string, warningDays: number, criticalDays: number) => http<Pick<BestBeforeState, "settings" | "summary">>("GET", `/api/tenants/${t}/stock/best-before${qs({ previewWarning: warningDays, previewCritical: criticalDays })}`),
    salesHistory: (t: string) => http<{ shops: SalesHistoryShop[] }>("GET", `/api/tenants/${t}/stock/sales-history`),
    loadSalesHistory: (t: string, channelId: string) => http<{ queued: boolean }>("POST", `/api/tenants/${t}/stock/sales-history/${channelId}`),
    reach: (t: string, q: ReachQuery) => http<ReachPage>("GET", `/api/tenants/${t}/stock/reach${qs(q)}`),
    slowMovers: (t: string, q: SlowMoverQuery) => http<SlowMoverPage>("GET", `/api/tenants/${t}/stock/slow-movers${qs(q)}`),
    slowMoversCsvUrl: (t: string, q: SlowMoverQuery) => `/api/tenants/${t}/stock/slow-movers.csv${qs({ ...q, page: undefined, pageSize: undefined })}`,
    setDiscontinued: (t: string, productId: string, discontinued: boolean) => http<{ discontinued: boolean }>("PUT", `/api/tenants/${t}/products/${productId}/discontinued`, { discontinued }),
    reachCsvUrl: (t: string, q: ReachQuery) => `/api/tenants/${t}/stock/reach.csv${qs({ ...q, page: undefined, pageSize: undefined })}`,
    replenishmentSettings: (t: string) => http<ReplenishmentSettings>("GET", `/api/tenants/${t}/settings/replenishment`),
    saveReplenishmentSettings: (t: string, s: ReplenishmentSettings) => http<ReplenishmentSettings>("PUT", `/api/tenants/${t}/settings/replenishment`, s),
    saveBestBeforeSettings: (t: string, s: { warningDays: number; criticalDays: number }) => http<{ warningDays: number; criticalDays: number }>("PUT", `/api/tenants/${t}/settings/best-before`, s),
  },
  imports: {
    templates: (t: string, kind: "orders" | "products") => http<CsvTemplate[]>("GET", `/api/tenants/${t}/import-templates${qs({ kind })}`),
    saveTemplate: (t: string, tpl: CsvTemplateInput) => http<CsvTemplate>("POST", `/api/tenants/${t}/import-templates`, tpl),
    deleteTemplate: (t: string, id: string) => http<{ deleted: boolean }>("DELETE", `/api/tenants/${t}/import-templates/${id}`),
    downloadUrl: (t: string, kind: "orders" | "products") => `/api/tenants/${t}/import-templates/download/${kind}`,
    previewOrders: (t: string, b: CsvImportBody) => http<OrderCsvPreview>("POST", `/api/tenants/${t}/orders/import/preview`, b),
    importOrders: (t: string, b: CsvImportBody) => http<{ created: number; duplicates: number; issues: CsvIssue[]; issueCount: number; unknownSkus: string[] }>("POST", `/api/tenants/${t}/orders/import`, b),
    previewProducts: (t: string, b: CsvImportBody) => http<ProductCsvPreview>("POST", `/api/tenants/${t}/products/csv-import/preview`, b),
    importProducts: (t: string, b: CsvImportBody) => http<{ created: number; updated: number; unchanged: number; blocked: { sku: string; reasons: string[] }[]; issues: CsvIssue[]; issueCount: number }>("POST", `/api/tenants/${t}/products/csv-import`, b),
  },
  inbounds: {
    list: (t: string, q: { q?: string; status?: string } = {}) => http<InboundItem[]>("GET", `/api/tenants/${t}/inbounds${qs(q)}`),
    page: (t: string, q: { q?: string; status?: string; page: number; pageSize?: 50 | 100 | 250 }) => http<Paged<InboundItem>>("GET", `/api/tenants/${t}/inbounds${qs(q)}`),
    create: (t: string, body: NewInbound) => http<{ id: string; inboundNumber: string }>("POST", `/api/tenants/${t}/inbounds`, body),
    setAppointment: (t: string, id: string, a: InboundAppointment | null) => http<{ appointment: InboundAppointment | null }>("PUT", `/api/tenants/${t}/inbounds/${id}/appointment`, a ?? { clear: true }),
  },
  dashboard: {
    get: (t: string) => http<{ layout: import("@logiful/domain").DashboardLayout; source: "user" | "team" | "default"; team: import("@logiful/domain").DashboardLayout | null }>("GET", `/api/tenants/${t}/dashboard`),
    save: (t: string, l: import("@logiful/domain").DashboardLayout) => http<{ layout: import("@logiful/domain").DashboardLayout }>("PUT", `/api/tenants/${t}/dashboard`, l),
    reset: (t: string) => http<void>("DELETE", `/api/tenants/${t}/dashboard`),
    saveTeam: (t: string, l: import("@logiful/domain").DashboardLayout) => http<{ team: import("@logiful/domain").DashboardLayout }>("PUT", `/api/tenants/${t}/dashboard/team`, l),
  },
  calendar: (t: string, from: string, to: string) => http<CalendarData>("GET", `/api/tenants/${t}/calendar${qs({ from, to })}`),
  announcements: (t: string) => http<Announcement[]>("GET", `/api/tenants/${t}/announcements`),
  rules: {
    get: (t: string, channelId: string) => http<RulesState>("GET", `/api/tenants/${t}/channels/${channelId}/rules`),
    preview: (t: string, channelId: string, def: OrderRuleSet) => http<RulesPreview>("POST", `/api/tenants/${t}/channels/${channelId}/rules/preview`, def),
    save: (t: string, channelId: string, def: OrderRuleSet) => http<{ version: number }>("POST", `/api/tenants/${t}/channels/${channelId}/rules`, { definition: def, activate: true }),
    activate: (t: string, channelId: string, version: number) => http<{ version: number }>("POST", `/api/tenants/${t}/channels/${channelId}/rules/${version}/activate`),
  },
};

export type Api = typeof httpApi;

export interface LoadRunReport {
  appEnv: "production" | "staging";
  run: { runId: string; orders: number; minutes: number; startedAt: string; created: number; finishedAt: string | null; error: string | null } | null;
  stats?: { created: number; handed_over: number; shipped: number; on_hold: number; failed: number; waiting: number; p50: number | null; p95: number | null; pmax: number | null; perMinute: number };
  holds?: { reason: string; n: number }[];
  queues?: { key: string; waiting?: number; active?: number; delayed?: number; failed?: number; prioritized?: number }[];
}

export const isDemo = import.meta.env.MODE === "mock" || import.meta.env.VITE_DEMO === "1";
export const api: Api = isDemo ? mockApi : httpApi;
